import { Component, OnInit, OnDestroy, AfterViewChecked, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, Observable } from 'rxjs';
import { 
  PartDetail, 
  PartFile, 
  QuantityPricing,
  MaterialItem,
  ToolItem,
  ProcessItem,
  AdditionalCostItem,
  QuoteAssistV2Data
} from '../../quote-assist-v2.model';
import {
  PartsChangeEvent,
  ComponentValidationState,
  ComponentConfig,
  PartUpdateEvent,
  FileUploadEvent
} from '../../models/component-events.model';
import { QuoteAssistV2Service, FileUploadResponse, MetadataExtractionResponse, CostBreakdownResponse, SaveFeedbackRequest, SaveFeedbackResponse } from '../../../../services/quote-assist-v2.service';
import { NumericInputV2Directive } from '../../../shared/directives/numeric-input-v2.directive';
import { FormatNumberPipe } from '../../../pipes/format.number.pipe';
import { MixPanelEvents } from '../../../shared/enums';
import { ToastrService } from 'ngx-toastr';
import * as ryersonPricing from '../../../../ryerson-avg-pricing.json';
import { ActivatedRoute } from '@angular/router';
import { QuoteAssistv2BaseComponent } from '../../main/quote-assist-v2-base.component';

@Component({
  selector: 'app-parts-information',
  standalone: true,
  imports: [CommonModule, FormsModule, NumericInputV2Directive, FormatNumberPipe],
  templateUrl: './parts-information.component.html',
  styleUrls: ['./parts-information.component.scss','../../main/quote-assist-v2.disabled-overlay.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush 
})
export class PartsInformationComponent extends QuoteAssistv2BaseComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
  @Input() parts: PartDetail[] = [];
  @Input() config!: ComponentConfig;
  @Input() validationState!: ComponentValidationState;
  @Input() quoteDetails: any = {}; 
  @Input() integrationType: 'MFG' | 'MFGDirect' | 'SUW' = 'MFG';
  @Input() quoteData?: QuoteAssistV2Data;
  @Input() showFileUploadForPartIndex?: number;
  @Input() isQuoteSubmitted: boolean = false;
  @Input() isEditMode: boolean = false;
  @Input() skipToConfiguration: boolean = false;
  @Input() contactId?: number; // Added for future functionality like PDF downloads
  @Input() companyId?: number;
  @Input() isViewMode: boolean = false;
  @Input() surveyDone?: boolean; // Survey feedback status from parent

  @Output() partsChange = new EventEmitter<PartsChangeEvent>();
  @Output() stepNavigation = new EventEmitter<any>();
  @Output() fileUploadRequest = new EventEmitter<number>();
  @Output() addNewPart = new EventEmitter<void>();
  @Output() currentPartIndexChange = new EventEmitter<number>();
  @Output() draftStateChange = new EventEmitter<boolean>();
  @Output() hasPartsChange = new EventEmitter<boolean>();
  @Output() isFileUploadViewChange = new EventEmitter<boolean>();
  @Output() watchDemo = new EventEmitter<void>();

  // Local state
  localParts: PartDetail[] = [];
  currentPartIndex: number = 0;
  currentView: 'file-upload' | 'configuration' = 'file-upload';
  private validationErrors: { [fieldName: string]: string } = {};
  private sectionValidationErrors: { [sectionName: string]: { [itemIndex: string]: { name: boolean; cost: boolean } } } = {};
  private subscriptions: Subscription[] = [];
  showHelp: boolean = false;
  isDataLoaded: boolean = true;
  private hasRetried: boolean = false;

  // Feedback Survey State
  showFeedbackSurvey = false;
  feedbackStep: 'rating' | 'comment' = 'rating';
  feedbackRating: 'positive' | 'negative' | null = null;
  feedbackComment = '';
  private pendingNavigation = false;
  isSubmittingFeedback = false; // Loading state for feedback submission
  
  // Static property to track survey across all instances
  private static surveyShownInSession = false;

  // View control for MFG integration - separate from data state
  forceShowInitialView: boolean = false;
  isFileUploading: boolean = false;
  showFileUploadForMFG: boolean = false;

  // Flag to prevent multiple draft state emissions
  private draftStateEmitted: boolean = false;

  // Flag to prevent double submission
  private isSubmitting: boolean = false;

  // Flag to control when LeadTime validation should run
  private shouldValidateLeadTime: boolean = false;

  // Section visibility for configuration view
  showMaterials = true;
  showTooling = true;
  showProcesses = true;
  showAdditionalCosts = true;

  // New item templates for configuration
  newMaterial: MaterialItem = { name: '', cost: null, costType: 'Per Unit', reference: '' };
  newTool: ToolItem = { name: '', cost: null, costType: 'Fixed', reference: '' };
  newProcess: ProcessItem = { name: '', machineCost: null, setupCost: null, machineCostType: 'Per Unit', setupCostType: 'Fixed', reference: '' };
  newAdditionalCost: AdditionalCostItem = { name: '', cost: null, costType: 'Fixed', reference: '' };

  // Cost types
  costTypes = ['Per Unit', 'Fixed'];
  // Leadtime units
  leadtimeUnits = ['Days', 'Weeks'];
  additionalCosts = [
    { name: 'Inspection', cost: null, costType: 'Fixed', reference: '' },
    { name: 'Overhead', cost: null, costType: 'Fixed', reference: '' }
  ];

  // Tooltip properties
  isTooltipVisible = false;
  tooltipPosition = { x: 0, y: 0 };
  tooltipText = '';

  // Validation rules
  private readonly validationRules: { [key: string]: any } = {
    partName: { required: true, minLength: 2, maxLength: 100 },
    partNumber: { required: true, minLength: 2, maxLength: 50 },
    files: { required: false, maxCount: 10 }
  };

  queryParams = {
    companyId: 0,
    rfqPartId: 0,
    contactId: 0,
    isTrackingEnabled: 'false',
    integration: '',
  };
  constructor(
    private cdr: ChangeDetectorRef,
    private quoteAssistV2Service: QuoteAssistV2Service,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService
  ) { super();}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['companyId']) {
        this.queryParams.companyId = Number(params['companyId']);
        this.queryParams.rfqPartId = Number(params['rfqPartId']);
        this.queryParams.contactId = Number(params['contactId']);
        this.queryParams.isTrackingEnabled = params['isTrackingEnabled'];
        this.queryParams.integration = params['integration'];
        // this.isTrackingEnabled = this.queryParams.isTrackingEnabled ;
      }
    });
    this.initializeComponent();
    
    // Check if we should show file upload view for a specific part
    if (this.showFileUploadForPartIndex !== undefined) {
      this.switchToPart(this.showFileUploadForPartIndex);
      this.setCurrentView('file-upload');
      this.cdr.detectChanges();
    }
    // Ensure proper display for MFG integration coming from quote details
    this.ensureInitialViewForMFG();
    // Force one more change detection cycle to ensure UI updates
    setTimeout(() => this.cdr.detectChanges(), 0);
    
  }

  ngOnChanges(changes: SimpleChanges): void {
    
    // Handle changes to parts input - this ensures real-time sync with tracker component
    if (changes['parts'] && changes['parts'].currentValue) {
      const newParts = changes['parts'].currentValue;
      const previousParts = changes['parts'].previousValue;
      
      // Always update local parts when parts input changes to ensure synchronization
      
      // Deep copy the new parts to ensure complete isolation
      this.localParts = newParts.map((part: PartDetail) => ({
        ...part,
        isPartDetailCompleted: part.isPartDetailCompleted || false,
        internalNotes: part.internalNotes || '',
        isInternalNotesEnabled: part.isInternalNotesEnabled || false,
        isContinueWithoutFiles: part.isContinueWithoutFiles || false,
        isValidated: part.isValidated || false,
        isValid: part.isValid || false,
        configuration: part.configuration ? {
          ...part.configuration,
          materials: part.configuration.materials || [],
          tools: part.configuration.tools || [],
          processes: part.configuration.processes || [],
          additionalCosts: part.configuration.additionalCosts || [],
          materialsQuantityBreakdownEnabled: part.configuration.materialsQuantityBreakdownEnabled || false
        } : {
          materials: [],
          tools: [],
          processes: [],
          additionalCosts: [],
          materialsQuantityBreakdownEnabled: false
        },
        quantityPricing: part.quantityPricing || [],
        files: part.files || [],
        notesForBuyer: part.notesForBuyer || '',
        extractedContent: part.extractedContent || '',
        risks: part.risks || [],
        riskCardData: part.riskCardData,
        riskCardName: part.riskCardName,
        riskCardDataRaw: part.riskCardDataRaw,
        riskFeedbackAction: part.riskFeedbackAction
      }));
      
      // Update current part index if it's out of bounds
      if (this.currentPartIndex >= this.localParts.length) {
        this.currentPartIndex = Math.max(0, this.localParts.length - 1);
      }
      
      // Emit changes to update parent state
      this.emitHasPartsChange();
      
      // Check if we should skip to configuration view when parts are loaded
      if (this.skipToConfiguration && this.integrationType === 'MFG' && this.currentView !== 'configuration' && !this.showFileUploadForMFG) {
        console.log('[PartsInformation] Parts loaded with skipToConfiguration flag, switching to configuration view');
        this.setCurrentView('configuration');
        
        // Ensure we're showing the configuration sections
        this.showMaterials = true;
        this.showTooling = true;
        this.showProcesses = true;
        this.showAdditionalCosts = true;
      }
      
      // Force change detection multiple times to ensure UI updates
     
      setTimeout(() => this.cdr.detectChanges(), 10);
      
    } else {
      console.log('[PartsInformation] No parts input changes detected');
    }
    
    // Handle changes to showFileUploadForPartIndex
    if (changes['showFileUploadForPartIndex'] && 
        changes['showFileUploadForPartIndex'].currentValue !== undefined) {
      const partIndex = changes['showFileUploadForPartIndex'].currentValue;
      
      if (partIndex >= 0 && partIndex < this.localParts.length) {
        this.switchToPart(partIndex);
        this.setCurrentView('file-upload');
        console.log(`[PartsInformation] Switched to file upload view for part ${partIndex}`);
        console.log(`[PartsInformation] Updated state - hasParts: ${this.hasParts}, isFileUploadView: ${this.isFileUploadView}`);
        this.cdr.detectChanges();
      } else {
        console.error(`[PartsInformation] Invalid part index: ${partIndex}. Available parts: ${this.localParts.length}`);
      }
    }
    
    // Handle changes to edit mode
    if (changes['isEditMode'] && changes['isEditMode'].currentValue) {
      console.log(`[PartsInformation] Edit mode enabled, switching to configuration view`);
      
      // When edit mode is enabled, ensure we're in configuration view
      if (this.currentView !== 'configuration') {
        this.setCurrentView('configuration');
      }
      
      // Ensure we're showing the configuration sections when in edit mode
      this.showMaterials = true;
      this.showTooling = true;
      this.showProcesses = true;
      this.showAdditionalCosts = true;
      
      // Force change detection to update UI
      this.cdr.detectChanges();
    }

    // Handle changes to skipToConfiguration flag
    if (changes['skipToConfiguration'] && changes['skipToConfiguration'].currentValue === true) {
      console.log(`[PartsInformation] skipToConfiguration flag set to true, switching to configuration view`);
      
      // When skipToConfiguration is true, ensure we're in configuration view
      if (this.currentView !== 'configuration') {
        this.setCurrentView('configuration');
      }
      
      // Ensure we're showing the configuration sections
      this.showMaterials = true;
      this.showTooling = true;
      this.showProcesses = true;
      this.showAdditionalCosts = true;
      
      // Force change detection to update UI
      this.cdr.detectChanges();
    }

    // Handle changes to integrationType
    if (changes['integrationType'] && changes['integrationType'].currentValue === 'MFG') {
      console.log(`[PartsInformation] Integration type changed to MFG, checking skipToConfiguration flag`);
      
      // If we have the skipToConfiguration flag set, ensure we're in configuration view
      if (this.skipToConfiguration && this.currentView !== 'configuration') {
        console.log(`[PartsInformation] MFG integration with skipToConfiguration, switching to configuration view`);
        this.setCurrentView('configuration');
        
        // Ensure we're showing the configuration sections
        this.showMaterials = true;
        this.showTooling = true;
        this.showProcesses = true;
        this.showAdditionalCosts = true;
        
        // Force change detection to update UI
        this.cdr.detectChanges();
      }
    }
  }

  ngAfterViewChecked(): void {
    // Check if configuration view is rendered and draft hasn't been enabled yet
    if (!this.draftStateEmitted && !this.quoteDetails?.isDraftEnabled && 
        this.hasParts && this.isConfigurationView) {
      // Use a small delay to ensure DOM is fully rendered before enabling draft
      setTimeout(() => {
        if (!this.draftStateEmitted && this.hasParts && this.isConfigurationView) {
          this.draftStateEmitted = true;
          this.draftStateChange.emit(true);
        }
      }, 10);
    }
  }

  /**
   * Update draft state based on configuration view condition
   * Emits true when configuration view HTML is actually rendered
   * Once enabled, stays enabled (sticky behavior)
   */
  private updateDraftState(): void {
    // Check if draft is already enabled in the quote details
    const isAlreadyEnabled = this.quoteDetails?.isDraftEnabled;
    
    // Only enable if not already enabled (one-way toggle)
    if (!isAlreadyEnabled) {
      const shouldEnableDraft = this.hasParts && this.isConfigurationView;
      if (shouldEnableDraft) {
        // Wait for DOM to render the configuration view before enabling draft
        setTimeout(() => {
          // Double-check the conditions after DOM update to ensure HTML is rendered
          if (this.hasParts && this.isConfigurationView) {
            this.draftStateChange.emit(true);
          }
        }, 0);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Clear LeadTime validation state when component is destroyed
    this.disableLeadTimeValidation();
  }

  // ===== INITIALIZATION =====

  /**
   * Ensures the Initial View (Quote Summary) displays for MFG integration
   * This method guarantees the three main divs are visible when coming from quote details
   */
  private ensureInitialViewForMFG(): void {
    if (this.integrationType === 'MFG') {
      // For MFG integration, check if we should skip to configuration view
      if (this.skipToConfiguration) {
        // Skip to configuration view and start quote assist immediately
        this.forceShowInitialView = false;
        this.setCurrentView('configuration');
        
        // Ensure we have parts and use the first part
        // if (this.localParts.length === 0) {
        //   this.localParts.push(this.createNewPart());
        // }
        
        // Set current part to first part and start quote assist
        for(let i = 0; i < this.localParts.length; i++){
          if(this.localParts[i].isActivePart){
            this.currentPartIndex = i;
            break;
          }
        }
        
        // Start quote assist immediately for MFG integration
        this.startQuoteAssist();
        
        console.log('[PartsInformation] MFG integration: Skipped to configuration view and started quote assist');
      } else {
      // For MFG integration, always show file upload view (not initial view)
      this.forceShowInitialView = false;
      this.setCurrentView('file-upload');
      
      // Create a default part if none exists
      if (this.localParts.length === 0) {
        this.localParts.push(this.createNewPart());
        }
      }
      
      // Trigger change detection to ensure the view displays
      this.cdr.detectChanges();
    }
  }

  private initializeComponent(): void {
    console.log('initializeComponent',this.parts);
    // For MFG integration coming from quote details, check if we should skip to configuration
    if (this.integrationType === 'MFG') {
      this.forceShowInitialView = false; // Don't show initial view for MFG
      
      // Check if we should skip to configuration view (e.g., when data is loaded from IndexedDB)
      if (this.skipToConfiguration) {
        this.setCurrentView('configuration');
        console.log('[PartsInformation] MFG integration: Skipping to configuration view due to skipToConfiguration flag');
      } else {
        this.setCurrentView('file-upload');
        console.log('[PartsInformation] MFG integration: Showing file upload view');
      }
      
      // Create a default part if none exists
      if (this.parts.length === 0) {
        this.localParts = [this.createNewPart()];
      } else {
        // Deep copy the input parts to ensure complete isolation
        this.localParts = this.parts.map(part => ({
          ...part,
          isPartDetailCompleted: part.isPartDetailCompleted || false, // Ensure the property exists
          internalNotes: part.internalNotes || '', // Ensure internalNotes is initialized
          isInternalNotesEnabled: part.isInternalNotesEnabled || false, // Ensure isInternalNotesEnabled is initialized
          isContinueWithoutFiles: part.isContinueWithoutFiles || false,
          isValidated: part.isValidated || false,
          isValid: part.isValid || false,
          configuration: part.configuration ? {
            materials: part.configuration.materials && part.configuration.materials.length > 0 
              ? [...(part.configuration.materials || [])]
              : [{ name: '', cost: 0, costType: 'Per Unit', reference: '' }], // Add empty material if array is empty
            tools: part.configuration.tools && part.configuration.tools.length > 0 
              ? [...(part.configuration.tools || [])]
              : [{ name: '', cost: 0, costType: 'Fixed', reference: '' }], // Add empty tool if array is empty
            processes: part.configuration.processes && part.configuration.processes.length > 0 
              ? [...(part.configuration.processes || [])]
              : [{ name: '', machineCost: 0, setupCost: 0, machineCostType: 'Per Unit', setupCostType: 'Fixed', reference: '' }], // Add empty process if array is empty
            additionalCosts: [...(part.configuration.additionalCosts || [])],
            materialsQuantityBreakdownEnabled: part.configuration.materialsQuantityBreakdownEnabled || false
          } : {
            materials: [{ name: '', cost: 0, costType: 'Per Unit', reference: '' }], // Start with one empty material
            tools: [{ name: '', cost: 0, costType: 'Fixed', reference: '' }], // Start with one empty tool
            processes: [],
            additionalCosts: [
              { name: 'Inspection', cost: 0, costType: 'Fixed', reference: '' },
              { name: 'Overhead', cost: 0, costType: 'Fixed', reference: '' }
            ],
            materialsQuantityBreakdownEnabled: false
          },
          risks: part.risks || [],
          riskCardData: part.riskCardData,
          riskCardName: part.riskCardName,
          riskCardDataRaw: part.riskCardDataRaw,
          riskFeedbackAction: part.riskFeedbackAction
        }));
      }
      
      this.cdr.detectChanges();
    } else {
      // Existing logic for other integrations
      if (this.parts.length === 0) {
        this.forceShowInitialView = true;
        this.setCurrentView('file-upload');
      } else {
        this.forceShowInitialView = false;
      }
      
      // Deep copy the input parts to ensure complete isolation
      this.localParts = this.parts.length > 0 ? this.parts.map(part => ({
        ...part,
        isPartDetailCompleted: part.isPartDetailCompleted || false, // Ensure the property exists
        internalNotes: part.internalNotes || '', // Ensure internalNotes is initialized
        isInternalNotesEnabled: part.isInternalNotesEnabled || false, // Ensure isInternalNotesEnabled is initialized
        isContinueWithoutFiles: part.isContinueWithoutFiles || false,
        isValidated: part.isValidated || false,
        isValid: part.isValid || false,
        configuration: part.configuration ? {
          materials: part.configuration.materials && part.configuration.materials.length > 0 
            ? [...(part.configuration.materials || [])]
            : [{ name: '', cost: 0, costType: 'Per Unit', reference: '' }], // Add empty material if array is empty
          tools: part.configuration.tools && part.configuration.tools.length > 0 
            ? [...(part.configuration.tools || [])]
            : [{ name: '', cost: 0, costType: 'Fixed', reference: '' }], // Add empty tool if array is empty
          processes: part.configuration.processes && part.configuration.processes.length > 0 
            ? [...(part.configuration.processes || [])]
            : [{ name: '', machineCost: 0, setupCost: 0, machineCostType: 'Per Unit', setupCostType: 'Fixed', reference: '' }], // Add empty process if array is empty
          additionalCosts: [...(part.configuration.additionalCosts || [])]
        } : {
          materials: [{ name: '', cost: 0, costType: 'Per Unit', reference: '' }], // Start with one empty material
          tools: [{ name: '', cost: 0, costType: 'Fixed', reference: '' }], // Start with one empty tool
          processes: [{ name: '', machineCost: null, setupCost: null, machineCostType: 'Per Unit', setupCostType: 'Fixed', reference: '' }],
          additionalCosts: [
            { name: 'Inspection', cost: 0, costType: 'Fixed', reference: '' },
            { name: 'Overhead', cost: 0, costType: 'Fixed', reference: '' }
          ]
        },
        risks: part.risks || [],
        riskCardData: part.riskCardData,
        riskCardName: part.riskCardName,
        riskCardDataRaw: part.riskCardDataRaw,
        riskFeedbackAction: part.riskFeedbackAction
      })) : [];
    }
    
    if (this.localParts.length > 0) {
      // Show configuration view for the first part (where files are likely to be)
      this.currentPartIndex = 0;
      
      // For MFG integration, keep file upload view; for others, show configuration view
      if (this.integrationType !== 'MFG') {
        this.setCurrentView('configuration');
      }
      
      // Recalculate pricing for all parts
      this.localParts.forEach((_, index) => {
        const tempCurrentIndex = this.currentPartIndex; // Save current index
        this.currentPartIndex = index;
        // this.recalculatePricingForCurrentPart();
        this.currentPartIndex = tempCurrentIndex; // Restore current index
      });
      
    } else {
      // Show file upload view for new part
      this.setCurrentView('file-upload');
    }
    
    this.validateAllParts();
    
    // Ensure LeadTime validation is disabled initially
    this.disableLeadTimeValidation();
  }

  private createNewPart(): PartDetail {
    return {
      partId: this.localParts.length + 1,
      partName: '',
      partNumber: '',
      files: [],
      notesForBuyer: '',
      internalNotes: '',
      isPartDetailCompleted: false,
      isInternalNotesEnabled: false,
      isContinueWithoutFiles: false,
      isValidated: false,
      isValid: false,
      isActivePart: false, // New parts are not active by default
      configuration: {
        materials: [
          { name: '', cost: null, costType: 'Per Unit' as 'Per Unit', reference: '' }
        ],
        tools: [
          { name: '', cost: null, costType: 'Fixed' as 'Fixed', reference: '' }
        ],
        processes: [
          { name: '', machineCost: null, setupCost: null, machineCostType: 'Fixed' as 'Fixed', setupCostType: 'Fixed' as 'Fixed', reference: '' }
        ],
        additionalCosts: [
          { name: 'Inspection', cost: null, costType: 'Fixed' as 'Fixed', reference: '' },
          { name: 'Overhead', cost: null, costType: 'Fixed' as 'Fixed', reference: '' }
        ],
        materialsQuantityBreakdownEnabled: false
      },
      quantityPricing: [
        { order: 1, quantity: 1, leadtimeValue: 0, leadtimeUnit: 'Days' as 'Days', unitCost: null, markup: null, unitPrice: null, total: 0, unitPriceTotal: null, isQuantityEditable: true, isValidQuantity: true, showLeadTimeInputFields: false },
        { order: 2, quantity: null, leadtimeValue: null, leadtimeUnit: 'Days' as 'Days', unitCost: null, markup: null, unitPrice: null, total: 0, unitPriceTotal: null, isQuantityEditable: true, isValidQuantity: true, showLeadTimeInputFields: false },
        { order: 3, quantity: null, leadtimeValue: null, leadtimeUnit: 'Days' as 'Days', unitCost: null, markup: null, unitPrice: null, total: 0, unitPriceTotal: null, isQuantityEditable: true, isValidQuantity: true, showLeadTimeInputFields: false }
      ]
    };
  }

  // ===== GETTERS =====

  /**
   * Check if a field is editable based on integration type
   */
  isFieldEditable(field: keyof PartDetail): boolean {
    if (this.integrationType === 'MFG' && (field === 'partName' || field === 'partNumber')) {
      return false;
    }
    return !this.config.readonly;
  }

  /**
   * Check if the current part is ready for MFG integration
   */
  isPartReadyForMFG(): boolean {
    if (this.integrationType !== 'MFG') {
      return true; // Not MFG integration, so always ready
    }
    
    return !!(this.currentPart.partName && this.currentPart.partName.trim().length > 0 &&
              this.currentPart.partNumber && this.currentPart.partNumber.trim().length > 0);
  }

  isQuantityEditable(pricing: QuantityPricing): boolean {
    if (this.integrationType !== 'MFG') {
      return true;
    }
    else{
      return (pricing.quantity !== null || pricing.quantity !== undefined) && pricing.isValidQuantity === true;
    }
  }
  isQuantityRowEditable(pricing: QuantityPricing): boolean {
    if(this.integrationType !== 'MFG'){
      return true;
    }
    else{
      return pricing.isQuantityEditable === true;
    }
  }

  isValidQuantity(pricing: QuantityPricing): boolean {
    return pricing.quantity !== null && pricing.quantity !== undefined;
  }

  get currentPart(): PartDetail {
    const part = this.localParts[this.currentPartIndex];
    if (!part) {
      return this.createNewPart();
    }
    // Ensure we return a reference to the actual part in the array
    return part;
  }

  /**
   * Check if current part has files uploaded for submission
   */
  get hasFilesForCurrentPartSubmission(): boolean {
    return this.currentPart.files.length > 0;
  }

  // Public method to manually sync parts from parent component
  public syncPartsFromParent(parts: PartDetail[]): void {
    // console.log('[PartsInformation] Manual sync requested with parts:', parts);
    if (parts && parts.length > 0) {
      this.localParts = parts.map((part: PartDetail) => ({
        ...part,
        isPartDetailCompleted: part.isPartDetailCompleted || false,
        internalNotes: part.internalNotes || '',
        isInternalNotesEnabled: part.isInternalNotesEnabled || false,
        isContinueWithoutFiles: part.isContinueWithoutFiles || false,
        isValidated: part.isValidated || false,
        isValid: part.isValid || false,
        configuration: part.configuration ? {
          ...part.configuration,
          materials: part.configuration.materials || [],
          tools: part.configuration.tools || [],
          processes: part.configuration.processes || [],
          additionalCosts: part.configuration.additionalCosts || [],
          materialsQuantityBreakdownEnabled: part.configuration.materialsQuantityBreakdownEnabled || false
        } : {
          materials: [],
          tools: [],
          processes: [],
          additionalCosts: []
        },
        quantityPricing: (part.quantityPricing || []).map(pricing => ({
          ...pricing,
          isLeadtimeEditing: false,
          isQuantityEditable: this.isQuantityEditable(pricing),
          isValidQuantity: this.isValidQuantity(pricing),
          showLeadTimeInputFields: pricing.showLeadTimeInputFields || false
        })),
        files: part.files || [],
        notesForBuyer: part.notesForBuyer || '',
        extractedContent: part.extractedContent || '',
        risks: part.risks || [],
        riskCardData: part.riskCardData,
        riskCardName: part.riskCardName,
        riskCardDataRaw: part.riskCardDataRaw,
        riskFeedbackAction: part.riskFeedbackAction
      }));
      
      console.log('[PartsInformation] Manual sync completed, local parts:', this.localParts);
      
      // Force change detection
      this.cdr.detectChanges();
      setTimeout(() => this.cdr.detectChanges(), 0);
    }
  }

  get hasParts(): boolean {
    // For MFG integration, respect the forceShowInitialView flag
    if (this.integrationType === 'MFG' && this.forceShowInitialView) {
      return false; // Force Initial View display
    }
    
    const hasPartsResult = this.localParts.length > 0;
    
    return hasPartsResult;
  }

  get isFileUploadView(): boolean {
    return this.currentView === 'file-upload' && !this.isEditMode;
  }

  get isConfigurationView(): boolean {
    return this.currentView === 'configuration' || this.isEditMode;
  }

  // ===== PART MANAGEMENT =====

  public setCurrentView(view: 'file-upload' | 'configuration'): void {
    if(this.integrationType === 'MFGDirect'){
      this.currentView = view;
    }else{
      this.currentView = 'configuration';
    }
    // this.isFileUploadViewChange.emit(this.isFileUploadView);
  }

  private emitHasPartsChange(): void {
    this.hasPartsChange.emit(this.hasParts);
  }

  onWatchDemoClick(): void {
    this.watchDemo.emit();
  }

  handleAddNewPart(): void {
    if(!this.validateAllParts()){
      this.emitPartsChange();
      this.currentPartIndexChange.emit(this.currentPartIndex);
      this.emitHasPartsChange();
      return;
    }
    const newPart = this.createNewPart();
    this.localParts.push(newPart);
    this.addTracking(MixPanelEvents.PartAdded, this.queryParams);
    this.currentPartIndex = this.localParts.length - 1;
    this.setCurrentView('file-upload');
    
    // Reset form state
    this.newMaterial = { name: '', cost: 0, costType: 'Per Unit', reference: '' };
    this.newTool = { name: '', cost: 0, costType: 'Fixed', reference: '' };
    this.newProcess = { name: '', machineCost: 0, setupCost: 0, machineCostType: 'Fixed', setupCostType: 'Fixed', reference: '' };
    this.newAdditionalCost = { name: '', cost: 0, costType: 'Fixed', reference: '' };
    
    // Recalculate pricing for the new part
    this.recalculatePricingForCurrentPart();
    
    // Clear LeadTime validation errors for the new part
    this.clearLeadTimeValidationErrors();
    
    
    this.emitPartsChange();
    this.addNewPart.emit();
    this.currentPartIndexChange.emit(this.currentPartIndex);
    this.emitHasPartsChange();
  }

  switchToPart(partIndex: number, view?: 'file-upload' | 'configuration'): void {
    if (partIndex >= 0 && partIndex < this.localParts.length) {
      
      this.currentPartIndex = partIndex;
      this.forceShowInitialView = false;
      if (view) {
        if(view === 'file-upload' && this.integrationType === 'MFGDirect'){
          this.showFileUploadForMFG = true;
        }
        this.setCurrentView(view);
      } else if (this.showFileUploadForPartIndex === undefined) {
        this.setCurrentView('configuration');
      }
      
      // Ensure the part has proper configuration structure
      if (!this.localParts[partIndex].configuration) {
        this.localParts[partIndex].configuration = {
          materials: [{ name: '', cost: 0, costType: 'Per Unit', reference: '' }],
          tools: [{ name: '', cost: 0, costType: 'Fixed', reference: '' }],
          processes: [{ name: '', machineCost: 0, setupCost: 0, machineCostType: 'Per Unit', setupCostType: 'Fixed', reference: '' }],
          additionalCosts: [
            { name: 'Inspection', cost: 0, costType: 'Fixed', reference: '' },
            { name: 'Overhead', cost: 0, costType: 'Fixed', reference: '' }
          ]
        };
      } else if (!this.localParts[partIndex].configuration.materials || this.localParts[partIndex].configuration.materials.length === 0) {
        // Ensure there's at least one empty material if the array is empty
        this.localParts[partIndex].configuration.materials = [{ name: '', cost: 0, costType: 'Per Unit', reference: '' }];
      }
      
      // Ensure there's at least one empty tool if the array is empty
      if (!this.localParts[partIndex].configuration.tools || this.localParts[partIndex].configuration.tools.length === 0) {
        this.localParts[partIndex].configuration.tools = [{ name: '', cost: 0, costType: 'Fixed', reference: '' }];
      }
      
      // Ensure there's at least one empty process if the array is empty
      if (!this.localParts[partIndex].configuration.processes || this.localParts[partIndex].configuration.processes.length === 0) {
        this.localParts[partIndex].configuration.processes = [{ name: '', machineCost: 0, setupCost: 0, machineCostType: 'Per Unit', setupCostType: 'Fixed', reference: '' }];
      }
      
      // Ensure internal notes properties are initialized
      if (this.localParts[partIndex].internalNotes === undefined) {
        this.localParts[partIndex].internalNotes = '';
      }
      if (this.localParts[partIndex].isInternalNotesEnabled === undefined) {
        this.localParts[partIndex].isInternalNotesEnabled = false;
      }
      // Clear LeadTime validation errors when switching parts
      this.clearLeadTimeValidationErrors();
      
      // this.validateAllParts();
      this.emitPartsChange();
      this.currentPartIndexChange.emit(this.currentPartIndex);
      this.emitHasPartsChange();

      // Auto-trigger AI data load when switching to configuration view
      const selectedPart = this.localParts[this.currentPartIndex];
      if (
        this.isConfigurationView &&
        selectedPart &&
        !selectedPart.isAiDataLoaded &&
        !this.isFileUploading
      ) {
        const hasPdf = (selectedPart.files || []).some(f =>
          (f.fileName && f.fileName.toLowerCase().endsWith('.pdf')) ||
          (f.fileURL && f.fileURL.toLowerCase().endsWith('.pdf')) ||
          (f.fileType && f.fileType.toLowerCase() === 'pdf')
        );
        if (hasPdf) {
          this.isFileUploading = true; // show loading overlay
          this.cdr.detectChanges();
          this.startQuoteAssist(true); // bypass name/number validation
        }
      }
      
      this.cdr.detectChanges();
    }
  }

  removePart(partIndex: number): void {
    if (this.localParts.length > 1) {
      this.localParts.splice(partIndex, 1);
      
      // Adjust current part index if needed
      if (this.currentPartIndex >= this.localParts.length) {
        this.currentPartIndex = this.localParts.length - 1;``
      }
      
      // Recalculate pricing for the current part after removal
      // this.recalculatePricingForCurrentPart();
      
      // Clear LeadTime validation errors after part removal
      this.clearLeadTimeValidationErrors();
      
      this.validateAllParts();
      this.emitPartsChange();
      this.currentPartIndexChange.emit(this.currentPartIndex);
      this.emitHasPartsChange();
    }
  }

  startAddingPart(): void {    
    // Disable force Initial View when user actually starts adding parts
    this.forceShowInitialView = false;
    
    this.setCurrentView('file-upload');
    this.currentPartIndex = this.localParts.length;
    this.localParts.push(this.createNewPart());
    
    // Clear LeadTime validation errors when starting to add a part
    this.clearLeadTimeValidationErrors();
        
    this.validateAllParts();
    this.emitPartsChange();
    this.addTracking(MixPanelEvents.PartAdded, this.queryParams);
    this.currentPartIndexChange.emit(this.currentPartIndex);
    this.emitHasPartsChange();
  }

  closeFileUpload(): void {
    // For MFG integration, don't remove parts without name/number as they should come from API
    if (this.integrationType === 'MFG') {
      this.setCurrentView('configuration');
    } else {
      // Remove the current part if it's empty (for non-MFG integrations)
      if (!this.currentPart.partName && !this.currentPart.partNumber && this.currentPart.files.length === 0) {
        this.localParts.splice(this.currentPartIndex, 1);
        if (this.localParts.length === 0) {
          this.setCurrentView('file-upload');
          this.currentPartIndex = 0;
        } else {
          this.currentPartIndex = this.localParts.length - 1;
          this.setCurrentView('configuration');
        }
      } else {
        this.setCurrentView('configuration');
      }
    }
    
    // Clear LeadTime validation errors when closing file upload
    this.clearLeadTimeValidationErrors();
    
    this.validateAllParts();
    this.emitPartsChange();
  }

  closeQuote(): void {
    // Navigate back to previous step
    this.stepNavigation.emit({
      fromStep: 'parts-information',
      toStep: 'details',
      canProceed: true
    });
  }

  submitPartFiles(): void {
    
    // For MFG integration, ensure part name and number are populated before proceeding
    if (this.integrationType === 'MFG' && !this.isPartReadyForMFG()) {
      console.warn('MFG integration: Cannot proceed without part name and number from API');
      return;
    }
    
    // if (this.validateCurrentPart()) {
      // Show loading overlay for quote assist process
      this.isFileUploading = true;
      this.cdr.detectChanges();
      
      this.setCurrentView('configuration');
      
      // Clear LeadTime validation errors when submitting part files
      this.clearLeadTimeValidationErrors();
      
      this.validateAllParts();
      this.emitPartsChange();
      
      // Use the new submission method instead of quote assist
      // this.submitAllPartsWithFiles();
      this.startQuoteAssist();
    // }
  }

  continueWithoutFiles(): void {
    // For MFG integration, ensure part name and number are populated before proceeding
    if (this.integrationType === 'MFG' && !this.isPartReadyForMFG()) {
      console.warn('MFG integration: Cannot proceed without part name and number from API');
      return;
    }
    this.currentPart.isContinueWithoutFiles = true;
    this.setCurrentView('configuration');
    
    // Clear LeadTime validation errors when continuing without files
    this.clearLeadTimeValidationErrors();
    
    this.validateAllParts();
    this.emitPartsChange();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent, sectionType: '3d' | '2d'): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Check if section already has a file
    if (sectionType === '3d' && this.has3DFile()) {
      return;
    }
    
    if (sectionType === '2d' && this.has2DFile()) {
      return;
    }
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      // Only take the first file for single file upload
      const fileArray = Array.from(files).slice(0, 1);
      this.uploadFiles(this.currentPartIndex, fileArray, sectionType);
    }
  }

  private validateCurrentPart(): boolean {
    // For MFG integration, part name and number are required and should be populated from API
    // if (this.integrationType === 'MFG') {
    //   const hasValidName = this.currentPart.partName && this.currentPart.partName.trim().length > 0;
    //   const hasValidNumber = this.currentPart.partNumber && this.currentPart.partNumber.trim().length > 0;
      
    //   if (!hasValidName || !hasValidNumber) {
    //     // Set validation errors for red styling
    //     const prefix = `part${this.currentPartIndex}`;
    //     if (!hasValidName) {
    //       this.validationErrors[`${prefix}_partName`] = 'Required';
    //     }
    //     if (!hasValidNumber) {
    //       this.validationErrors[`${prefix}_partNumber`] = 'Required';
    //     }
    //     return false;
    //   }
    // }
    
    // Basic validation for part name and number
    const hasValidName = this.currentPart.partName && this.currentPart.partName.trim().length > 0;
    const hasValidNumber = this.currentPart.partNumber && this.currentPart.partNumber.trim().length > 0;
    
    if (!hasValidName || !hasValidNumber) {
      // Set validation errors for red styling
      const prefix = `part${this.currentPartIndex}`;
      if (!hasValidName) {
        this.validationErrors[`${prefix}_partName`] = 'Required';
      }
      if (!hasValidNumber) {
        this.validationErrors[`${prefix}_partNumber`] = 'Required';
      }
      return false;
    }
    
    // Clear validation errors when fields are valid
    const prefix = `part${this.currentPartIndex}`;
    delete this.validationErrors[`${prefix}_partName`];
    delete this.validationErrors[`${prefix}_partNumber`];
    delete this.validationErrors[`${prefix}_quantity`];
    
    // Validate that at least one pricing has quantity > 0
    if (this.currentPart.quantityPricing && this.currentPart.quantityPricing.length > 0) {
      const hasValidQuantity = this.currentPart.quantityPricing.some(pricing => pricing.quantity && pricing.quantity > 0);
      if (!hasValidQuantity) {
        // Set validation error for quantity
        const prefix = `part${this.currentPartIndex}`;
        this.validationErrors[`${prefix}_quantity`] = 'At least one quantity must be greater than 0';
        return false;
      }
      
      // Validate total, unitPrice, and unitPriceTotal
      const hasValidTotal = this.currentPart.quantityPricing.some(pricing => pricing.total && pricing.total > 0);
      if (!hasValidTotal) {
        const prefix = `part${this.currentPartIndex}`;
        this.validationErrors[`${prefix}_total`] = 'At least one total must be greater than 0';
        return false;
      }
      
      const hasValidUnitPrice = this.currentPart.quantityPricing.some(pricing => pricing.unitPrice && pricing.unitPrice > 0);
      if (!hasValidUnitPrice) {
        const prefix = `part${this.currentPartIndex}`;
        this.validationErrors[`${prefix}_unitPrice`] = 'At least one unit price must be greater than 0';
        return false;
      }
      
      const hasValidUnitPriceTotal = this.currentPart.quantityPricing.some(pricing => pricing.unitPriceTotal && pricing.unitPriceTotal > 0);
      if (!hasValidUnitPriceTotal) {
        const prefix = `part${this.currentPartIndex}`;
        this.validationErrors[`${prefix}_unitPriceTotal`] = 'At least one unit price total must be greater than 0';
        return false;
      }
    }
    
    // Validate LeadTime when Part Quantity is present - only if validation is explicitly requested
    if (this.shouldValidateLeadTime && this.currentPart.quantityPricing && this.currentPart.quantityPricing.length > 0) {
      let hasLeadTimeError = false;
      
      for (const pricing of this.currentPart.quantityPricing) {
        // If quantity is present, leadtimeValue must also be present and valid
        if (pricing.quantity && pricing.quantity > 0) {
          if (!pricing.leadtimeValue || pricing.leadtimeValue <= 0) {
            hasLeadTimeError = true;
            break;
          }
        }
      }
      
      if (hasLeadTimeError) {
        // Set validation error for LeadTime
        const prefix = `part${this.currentPartIndex}`;
        this.validationErrors[`${prefix}_leadtime`] = 'Required';
        return false;
      }
    }
    
    return true;
  }

  onPartFieldChange(partIndex: number, field: keyof PartDetail, value: any): void {
    
    // Prevent changes to partName and partNumber for MFG integration
    if (this.integrationType === 'MFG' && (field === 'partName' || field === 'partNumber')) {
      console.log(`[PartsInformation] Field ${field} is read-only for MFG integration`);
      return;
    }
    
    // Validate for duplicates before updating
    const validationResult = this.validatePartField(partIndex, field, value);
    
    if (validationResult.isValid) {
      (this.localParts[partIndex] as any)[field] = value;
      
      // Clear validation error when validation passes
      delete this.validationErrors[`part${partIndex}_${field}`];
      
      // Emit cleared validation errors to parent so progress-tracker reflects changes
      this.emitValidationErrors();
      
      const event: PartUpdateEvent = {
        partIndex,
        field,
        value,
        part: this.localParts[partIndex]
      };

      this.validatePart(partIndex);
      this.emitPartsChange();
      this.logEvent('Part Updated', event);
      
      console.log(`[PartsInformation] Part field updated successfully: part ${partIndex}, field: ${field}, value: ${value}`);
    } else {
      // Store validation error
      this.validationErrors[`part${partIndex}_${field}`] = validationResult.error || '';
      
      // Emit validation errors to parent so progress-tracker can show them
      this.emitValidationErrors();
      
      console.log(`[PartsInformation] Part field validation failed: part ${partIndex}, field: ${field}, error: ${validationResult.error}`);
    }
  }

  validatePartField(partIndex: number, field: keyof PartDetail, value: any): { isValid: boolean; error?: string } {
    if (field === 'partName' || field === 'partNumber') {
      const trimmedValue = value?.trim();
      
      // For MFG integration, prevent clearing these fields
      if (this.integrationType === 'MFG' && (!trimmedValue || trimmedValue.length === 0)) {
        return { isValid: false, error: `${field === 'partName' ? 'Part Name' : 'Part Number'} cannot be cleared for MFG integration` };
      }
      
      if (!trimmedValue) {
        return { isValid: false, error: `${field === 'partName' ? 'Part Name' : 'Part Number'} is required` };
      }

      // Check for duplicates (excluding current part)
      const isDuplicate = this.localParts.some((part, index) => {
        if (index === partIndex) return false; // Skip current part
        
        if (field === 'partName') {
          return part.partName?.trim().toLowerCase() === trimmedValue.toLowerCase();
        } else {
          return part.partNumber?.trim().toLowerCase() === trimmedValue.toLowerCase();
        }
      });

      if (isDuplicate) {
        const fieldName = field === 'partName' ? 'Part Name' : 'Part Number';
        return { isValid: false, error: `${fieldName} already exists` };
      }

      // Additional validation rules
      if (field === 'partName') {
        if (trimmedValue.length < 2) {
          return { isValid: false, error: 'Part Name must be at least 2 characters' };
        }
        if (trimmedValue.length > 100) {
          return { isValid: false, error: 'Part Name must be no more than 100 characters' };
        }
      } else if (field === 'partNumber') {
        if (trimmedValue.length < 2) {
          return { isValid: false, error: 'Part Number must be at least 2 characters' };
        }
        if (trimmedValue.length > 50) {
          return { isValid: false, error: 'Part Number must be no more than 50 characters' };
        }
      }
    }

    return { isValid: true };
  }

  // ===== FILE MANAGEMENT =====

  /**
   * Check if 3D section already has a file
   */
  has3DFile(): boolean {
    return this.currentPart.files.filter(file => file.fileType === 'Model3D').length > 0;
  }

  /**
   * Check if 2D section already has a file
   */
  has2DFile(): boolean {
    return this.currentPart.files.filter(file => file.fileType === 'Drawing2D').length > 0;
  }

  /**
   * Validate if a file is a 3D model file
   */
  private is3DFile(fileName: string): boolean {
    const extension = fileName.toLowerCase().split('.').pop() || '';
    const modelTypes = ['step', 'stp', 'sldprt', 'igs', 'iges', 'ipt', 'x_t', 'stl', 'obj', '3mf'];
    return modelTypes.includes(extension);
  }

  /**
   * Validate if a file is a 2D drawing file
   */
  private is2DFile(fileName: string): boolean {
    const extension = fileName.toLowerCase().split('.').pop() || '';
    const pdfTypes = ['pdf'];
    const drawingTypes = ['dwg', 'dxf', 'slddrw'];
    const imageTypes = ['png', 'jpg', 'jpeg', 'bmp', 'tif', 'tiff'];
    return pdfTypes.includes(extension) || drawingTypes.includes(extension) || imageTypes.includes(extension);
  }

  /**
   * Validate files based on the upload section type
   */
  private validateFilesForSection(files: File[], sectionType: '3d' | '2d'): { validFiles: File[], invalidFiles: string[] } {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach(file => {
      if (sectionType === '3d') {
        if (this.is3DFile(file.name)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      } else if (sectionType === '2d') {
        if (this.is2DFile(file.name)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      }
    });

    return { validFiles, invalidFiles };
  }

  onFileUploadRequest(partIndex: number, sectionType?: '3d' | '2d'): void {
    // Check if section already has a file
    if (sectionType === '3d' && this.has3DFile()) {
      return;
    }
    
    if (sectionType === '2d' && this.has2DFile()) {
      return;
    }

    // Create a hidden file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = false; // Only allow single file selection
    
    // Set accept attribute based on section type
    if (sectionType === '3d') {
      fileInput.accept = '.step,.stp,.sldprt,.igs,.iges,.ipt,.x_t,.stl,.obj,.3mf';
    } else if (sectionType === '2d') {
      fileInput.accept = '.pdf,.dwg,.dxf,.slddrw,.png,.jpg,.jpeg,.bmp,.tif,.tiff';
    } else {
      // Default: accept all file types
      fileInput.accept = '.step,.stp,.sldprt,.igs,.iges,.ipt,.x_t,.stl,.obj,.3mf,.pdf,.dwg,.dxf,.slddrw,.png,.jpg,.jpeg,.bmp,.tif,.tiff';
    }
    
    fileInput.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.uploadFiles(partIndex, Array.from(files), sectionType);
      }
    };
    
    fileInput.click();
  }

  private createFormDataForUpload(partIndex: number, files: File[]): FormData {
    const formData = new FormData();
    const part = this.localParts[partIndex];
    
    // Add basic part information
    formData.append('CompanyId', this.quoteDetails.companyId?.toString() || '');
    formData.append('RfqId', this.quoteDetails.rfqId?.toString() || '');
    formData.append('RfqName', this.quoteDetails.rfqName || '');
    formData.append('QuoteDate', this.quoteDetails.quoteExpiryDate || '');
    formData.append('CreatedOn', new Date().toISOString());
    formData.append('Source', this.integrationType);
    
    // Add buyer information
    formData.append('BuyerCompanyName', this.quoteDetails.companyName || '');
    formData.append('BuyerCompanyAddress', this.quoteDetails.address || '');
    formData.append('BuyerCompanyEmail', this.quoteDetails.companyEmail || '');
    formData.append('BuyerCompanyPhone', this.quoteDetails.companyPhone || '');
    formData.append('BuyerCompanyCity', this.quoteDetails.city || '');
    formData.append('BuyerCompanyState', this.quoteDetails.state || '');
    formData.append('BuyerCompanyZipCode', this.quoteDetails.zipCode || '');
    
    // Add contact ID if available
    if (this.quoteDetails.contactId) {
      formData.append('ContactId', this.quoteDetails.contactId.toString());
    }
    
    // Add part details in array format
    formData.append(`RfqPartDetails[${partIndex}].IsUpdated`, 'false');
    formData.append(`RfqPartDetails[${partIndex}].Qty1`, '1');
    formData.append(`RfqPartDetails[${partIndex}].PartName`, part.partName || '');
    formData.append(`RfqPartDetails[${partIndex}].PartNumber`, part.partNumber || '');
    formData.append(`RfqPartDetails[${partIndex}].NotesForBuyer`, part.notesForBuyer || '');
    formData.append(`RfqPartDetails[${partIndex}].InternalNotes`, part.internalNotes || '');
    // formData.append(`RfqPartDetails[${partIndex}].PartId`, partIndex.toString());
    
    // Add files for this specific part
    files.forEach((file, fileIndex) => {
      formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].File`, file);
      formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileName`, file.name);
      formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].IsUpdated`, 'true');
      formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileType`, this.getFileType(file.name));
    });
    
    return formData;
  }

  /**
   * Create FormData for all parts (Complete Array Format)
   * Use this when you want to send all parts at once
   */
  private createFormDataForAllParts(): FormData {
    const formData = new FormData();
    
    // Add basic information (same for all parts)
    formData.append('CompanyId', this.quoteDetails.companyId?.toString() || '');
    formData.append('RfqId', this.quoteDetails.rfqId?.toString() || '');
    formData.append('RfqName', this.quoteDetails.rfqName || '');
    formData.append('QuoteDate', this.quoteDetails.quoteExpiryDate || '');
    formData.append('CreatedOn', new Date().toISOString());
    formData.append('Source', this.integrationType);
    
    // Add buyer information
    formData.append('BuyerCompanyName', this.quoteDetails.companyName || '');
    formData.append('BuyerCompanyAddress', this.quoteDetails.address || '');
    formData.append('BuyerCompanyEmail', this.quoteDetails.companyEmail || '');
    formData.append('BuyerCompanyPhone', this.quoteDetails.companyPhone || '');
    formData.append('BuyerCompanyCity', this.quoteDetails.city || '');
    formData.append('BuyerCompanyState', this.quoteDetails.state || '');
    formData.append('BuyerCompanyZipCode', this.quoteDetails.zipCode || '');
    
    // Add contact ID if available
    if (this.quoteDetails.contactId) {
      formData.append('ContactId', this.quoteDetails.contactId.toString());
    }
    
    // Add all parts as array
    this.localParts.forEach((part, partIndex) => {
      // Basic part information
      formData.append(`RfqPartDetails[${partIndex}].IsUpdated`, 'false');
      formData.append(`RfqPartDetails[${partIndex}].PartName`, part.partName || '');
      formData.append(`RfqPartDetails[${partIndex}].PartNumber`, part.partNumber || '');
      formData.append(`RfqPartDetails[${partIndex}].NotesForBuyer`, part.notesForBuyer || '');
      formData.append(`RfqPartDetails[${partIndex}].InternalNotes`, part.internalNotes || '');
      
      // Quantity pricing array
      part.quantityPricing.forEach((pricing, pricingIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].QuantityPricing[${pricingIndex}].Quantity`, (pricing.quantity || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].QuantityPricing[${pricingIndex}].LeadtimeValue`, pricing.leadtimeValue?.toString() || '');
        formData.append(`RfqPartDetails[${partIndex}].QuantityPricing[${pricingIndex}].LeadtimeUnit`, pricing.leadtimeUnit || '');
        formData.append(`RfqPartDetails[${partIndex}].QuantityPricing[${pricingIndex}].UnitCost`, (pricing.unitCost || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].QuantityPricing[${pricingIndex}].UnitPrice`, (pricing.unitPrice || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].QuantityPricing[${pricingIndex}].Total`, pricing.total.toString());
      });
      
      // Configuration - Materials
      part.configuration.materials.forEach((material, materialIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Materials[${materialIndex}].Name`, material.name);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Materials[${materialIndex}].Cost`, (material.cost || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Materials[${materialIndex}].CostType`, material.costType);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Materials[${materialIndex}].Reference`, material.reference);
      });
      
      // Configuration - Tools
      part.configuration.tools.forEach((tool, toolIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Tools[${toolIndex}].Name`, tool.name);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Tools[${toolIndex}].Cost`, (tool.cost || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Tools[${toolIndex}].CostType`, tool.costType);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Tools[${toolIndex}].Reference`, tool.reference);
      });
      
      // Configuration - Processes
      part.configuration.processes.forEach((process, processIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Processes[${processIndex}].Name`, process.name);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Processes[${processIndex}].MachineCost`, (process.machineCost || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Processes[${processIndex}].SetupCost`, (process.setupCost || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Processes[${processIndex}].MachineCostType`, process.machineCostType);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Processes[${processIndex}].SetupCostType`, process.setupCostType);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.Processes[${processIndex}].Reference`, process.reference);
      });
      
      // Configuration - Additional Costs
      part.configuration.additionalCosts.forEach((additionalCost, additionalIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Configuration.AdditionalCosts[${additionalIndex}].Name`, additionalCost.name);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.AdditionalCosts[${additionalIndex}].Cost`, (additionalCost.cost || 0).toString());
        formData.append(`RfqPartDetails[${partIndex}].Configuration.AdditionalCosts[${additionalIndex}].CostType`, additionalCost.costType);
        formData.append(`RfqPartDetails[${partIndex}].Configuration.AdditionalCosts[${additionalIndex}].Reference`, additionalCost.reference);
      });
      
      // Files for this part
      part.files.forEach((file, fileIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileName`, file.fileName);
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileType`, file.fileType);
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileURL`, file.fileURL);
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].IsUpdated`, 'false');
      });
    });
    
    return formData;
  }

  /**
   * Alternative method: Create individual form fields structure
   * This method creates the same structure as the main methods but as a separate implementation
   */
  private createFormDataWithJsonParts(): FormData {
    const formData = new FormData();
    
    // Add basic information
    formData.append('CompanyId', this.quoteDetails.companyId?.toString() || '');
    formData.append('RfqId', this.quoteDetails.rfqId?.toString() || '');
    formData.append('RfqName', this.quoteDetails.rfqName || '');
    formData.append('QuoteDate', this.quoteDetails.quoteExpiryDate || '');
    formData.append('CreatedOn', new Date().toISOString());
    formData.append('Source', this.integrationType);
    
    // Add buyer information
    formData.append('BuyerCompanyName', this.quoteDetails.companyName || '');
    formData.append('BuyerCompanyAddress', this.quoteDetails.address || '');
    formData.append('BuyerCompanyEmail', this.quoteDetails.companyEmail || '');
    formData.append('BuyerCompanyPhone', this.quoteDetails.companyPhone || '');
    formData.append('BuyerCompanyCity', this.quoteDetails.city || '');
    formData.append('BuyerCompanyState', this.quoteDetails.state || '');
    formData.append('BuyerCompanyZipCode', this.quoteDetails.zipCode || '');
    
    // Add contact ID if available
    if (this.quoteDetails.contactId) {
      formData.append('ContactId', this.quoteDetails.contactId.toString());
    }
    
    // Add all parts as individual form fields
    this.localParts.forEach((part, index) => {
      // Material specifications (extract from configuration)
      const materialSpecs = part.configuration.materials
        .filter(material => material.name && material.name.trim())
        .map(material => `${material.name}: ${material.cost} ${material.costType}`)
        .join(', ');
      
      // Finish array (from additional costs)
      const finishOptions = part.configuration.additionalCosts
        .filter(cost => cost.name && cost.name.trim())
        .map(cost => cost.name);
      
      // Quantity fields (from quantity pricing)
      const qty1 = part.quantityPricing[0]?.quantity || 0;
      const qty2 = part.quantityPricing[1]?.quantity || 0;
      const qty3 = part.quantityPricing[2]?.quantity || 0;
      
      // Add part details as individual form fields
      formData.append(`RfqPartDetails[${index}].PartId`, index.toString());
      formData.append(`RfqPartDetails[${index}].PartName`, part.partName || '');
      formData.append(`RfqPartDetails[${index}].PartNumber`, part.partNumber || '');
      formData.append(`RfqPartDetails[${index}].Notes`, part.notesForBuyer || '');
      formData.append(`RfqPartDetails[${index}].InternalNotes`, part.internalNotes || '');
      formData.append(`RfqPartDetails[${index}].MaterialSpecifications`, materialSpecs);
      formData.append(`RfqPartDetails[${index}].MaterialGrade`, 'Standard');
      formData.append(`RfqPartDetails[${index}].Tolerance`, 'Standard');
      formData.append(`RfqPartDetails[${index}].Qty1`, qty1.toString());
      formData.append(`RfqPartDetails[${index}].Qty2`, qty2.toString());
      formData.append(`RfqPartDetails[${index}].Qty3`, qty3.toString());
      
      // Add finish options as individual array elements
      finishOptions.forEach((finish, finishIndex) => {
        formData.append(`RfqPartDetails[${index}].Finish[${finishIndex}]`, finish);
      });
      
      // Add files for this part
      part.files.forEach((file, fileIndex) => {
        formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileName`, file.fileName);
        formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileUrl`, file.fileURL);
        formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileType`, file.fileType);
        formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].IsUpdated`, 'false');
      });
    });
    
    return formData;
  }

  private getFileType(fileName: string): string {
    const extension = fileName.toLowerCase().split('.').pop() || '';
    const pdfTypes = ['pdf'];
    const drawingTypes = ['dwg', 'dxf', 'slddrw'];
    const imageTypes = ['png', 'jpg', 'jpeg', 'bmp', 'tif', 'tiff'];
    const modelTypes = ['step', 'stp', 'sldprt', 'igs', 'iges', 'ipt', 'x_t', 'stl', 'obj', '3mf'];
    
    if (pdfTypes.includes(extension)) return 'Drawing2D';
    if (drawingTypes.includes(extension)) return 'Drawing2D';
    if (imageTypes.includes(extension)) return 'Drawing2D';
    if (modelTypes.includes(extension)) return 'Model3D';
    
    return 'Drawing2D'; // Default
  }

  private uploadFiles(partIndex: number, files: File[], sectionType?: '3d' | '2d'): void {
    this.isFileUploading = true;
    this.cdr.detectChanges();
    
    // Check if section already has a file
    if (sectionType === '3d' && this.has3DFile()) {
      this.isFileUploading = false;
      this.cdr.detectChanges();
      return;
    }
    
    if (sectionType === '2d' && this.has2DFile()) {
      this.isFileUploading = false;
      this.cdr.detectChanges();
      return;
    }
    
    // Validate files based on section type if provided
    let filesToUpload = files;
    let invalidFiles: string[] = [];
    
    if (sectionType) {
      const validation = this.validateFilesForSection(files, sectionType);
      filesToUpload = validation.validFiles;
      invalidFiles = validation.invalidFiles;
      
      // Show warning for invalid files
      if (invalidFiles.length > 0) {
        const sectionName = sectionType === '3d' ? '3D Model' : '2D Drawing';
        const invalidFileNames = invalidFiles.join(', ');
      }
      
      // If no valid files, return early
      if (filesToUpload.length === 0) {
        this.isFileUploading = false;
        this.cdr.detectChanges();
        return;
      }
    }
    
    const formData = this.createFormDataForFileUpload(partIndex, filesToUpload);
    
    this.quoteAssistV2Service.uploadPartFiles(formData).subscribe({
      next: (response: FileUploadResponse) => {
        if (response.success) {
          // Extract file URLs from the response
          let fileUrls: string[] = [];
          this.addTracking(MixPanelEvents.PartFileAdded, this.queryParams);
          // First try to get file URLs from rfqPartDetails structure
          if (response.data.rfqPartDetails && response.data.rfqPartDetails.length > 0) {
            // Find the part that matches our current part index
            const matchingPart = response.data.rfqPartDetails.find((part: any, index: number) => index === partIndex);
            if (matchingPart && matchingPart.files && Array.isArray(matchingPart.files)) {
              fileUrls = matchingPart.files.map((file: any) => file.fileUrl || '').filter((url: string) => url);
            }
          }

          // Fall back to the old fileUrls array if no URLs found in rfqPartDetails
          if (fileUrls.length === 0 && response.data.fileUrls) {
            fileUrls = response.data.fileUrls;
          }

          // Add files to the part with the correct URLs
          const uploadedFiles: PartFile[] = filesToUpload.map((file, index) => ({
            fileName: file.name,
            fileType: this.getFileType(file.name),
            fileURL: fileUrls[index] || '',
            extractedContent: response.data.extractedContent || ''
          }));
          
          this.localParts[partIndex].files.push(...uploadedFiles);
          
          // Save updated data to IndexedDB with file URLs
          this.saveDataToIndexedDB();
          
          // Force change detection to ensure UI updates
          this.cdr.detectChanges();
          
          this.validatePart(partIndex);
          this.emitPartsChange();
        }
        
        // Hide loading state
        this.isFileUploading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        // Hide loading state on error
        this.isFileUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Save updated data to IndexedDB after file upload
   */
  private saveDataToIndexedDB(): void {
    if (!this.quoteData) {
      console.warn('No quote data available for IndexedDB save');
      return;
    }

    // Create updated quote data with current parts
    const updatedQuoteData: QuoteAssistV2Data = {
      ...this.quoteData,
      partDetails: [...this.localParts],
      lastUpdated: new Date().toISOString()
    };

    // Save to IndexedDB using the service
    this.quoteAssistV2Service.saveQuoteAssistV2Progress(updatedQuoteData).subscribe({
      next: (rfqId) => {
        console.log('Data saved to IndexedDB successfully after file upload:', rfqId);
      },
      error: (error) => {
        console.error('Failed to save data to IndexedDB after file upload:', error);
      }
    });
  }

  private processPdfFiles(partIndex: number, pdfFiles: File[], extractedContent?: string): void {
    // For MFGDirect and SUW integrations, extract metadata from PDFs
    if ((this.integrationType === 'MFGDirect' || this.integrationType === 'SUW') && pdfFiles.length > 0) {
      this.extractPdfMetadata(partIndex, pdfFiles[0]);
    }
    
    // For all integrations, get cost breakdown if we have extracted content
    // if (extractedContent) {
    //   this.getCostBreakdown(partIndex, extractedContent);
    // }
  }

  private extractPdfMetadata(partIndex: number, pdfFile: File): void {
    this.isFileUploading = true;
    this.cdr.detectChanges();
    
    const formData = new FormData();
    formData.append('file', pdfFile);
    
    this.quoteAssistV2Service.extractPdfMetadata(formData).subscribe({
      next: (response: MetadataExtractionResponse) => {
        if (response.success && response.data.extractedContent) {
          // For MFGDirect integration, set metadata with combined supplier and extracted content
          if (this.integrationType === 'MFGDirect') {
            this.setMetadataForMfgDirect(response.data.extractedContent);
          }
          
          // Get cost breakdown using extracted content
          // this.getCostBreakdown(partIndex, response.data.extractedContent);
        } else {
          console.warn('Response success or extractedContent is falsy:', {
            success: response.success,
            extractedContent: response.data?.extractedContent,
            hasExtractedContent: !!response.data?.extractedContent
          });
        }
        
        // Hide loading state
        this.isFileUploading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error extracting PDF metadata:', error);
        // Hide loading state on error
        this.isFileUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private getCostBreakdown(partIndex: number, extractedContent: string): void {
    // Show loading state for cost breakdown generation
    this.isFileUploading = true;
    this.cdr.detectChanges();
    
    this.quoteAssistV2Service.getCostBreakdown(extractedContent).subscribe({
      next: (response: CostBreakdownResponse) => {
        if (response.success) {
          this.updatePartWithCostBreakdown(partIndex, response.data.costBreakdown);
        }
        
        // Hide loading state
        this.isFileUploading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error generating cost breakdown:', error);
        // Hide loading state on error
        this.isFileUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private updatePartWithCostBreakdown(partIndex: number, costBreakdown: any): void {
    const part = this.localParts[partIndex];
    
    // Update materials if available
    if (costBreakdown.materials && Array.isArray(costBreakdown.materials.materialsList)) {
      part.configuration.materials = costBreakdown.materials.materialsList.map((material: any) => ({
        name: material.name || '',
        cost: null,
        costType: 'Per Unit' as 'Per Unit' | 'Fixed'
        // reference: material.description || ''
      }));
    }
    
    // Update tools if available
    if (costBreakdown.tooling && Array.isArray(costBreakdown.tooling.toolingList)) {
      part.configuration.tools = costBreakdown.tooling.toolingList.map((tool: any) => ({
        name: null,
        cost: null,
        costType: 'Fixed' as 'Per Unit' | 'Fixed'
        // reference: tool.description || ''
      }));
    }
    
    // Update processes if available
    if (costBreakdown.manufacturingProcess && Array.isArray(costBreakdown.manufacturingProcess.manufacturingList)) {
      part.configuration.processes = costBreakdown.manufacturingProcess.manufacturingList.map((process: any) => ({
        name: null,
        machineCost: null,
        setupCost: null,
        machineCostType: 'Per Unit' as 'Per Unit' | 'Fixed',
        setupCostType: 'Fixed' as 'Per Unit' | 'Fixed'
        // reference: process.description || ''
      }));
    }

    //Update additional requirements if available
    if (costBreakdown.additionalRequirements && Array.isArray(costBreakdown.additionalRequirements.additionalRequirementList)) {
      part.configuration.additionalCosts = costBreakdown.additionalRequirements.additionalRequirementList.map((additionalCost: any) => ({
        name: additionalCost.processName || '',
        cost: additionalCost.estimatedCost === 'NULL' ? null : (additionalCost.estimatedCost || null),
        costType: 'Fixed' as 'Per Unit' | 'Fixed'
      }));
    }
    
    // Mark AI data as loaded for this part
    part.isAiDataLoaded = true;
    
    // Sync with main quoteData object to ensure data persistence across steps
    if (this.quoteData && this.quoteData.partDetails && this.quoteData.partDetails[partIndex]) {
      const existingPart = this.quoteData.partDetails[partIndex];
      this.quoteData.partDetails[partIndex] = {
        ...part,
        risks: part.risks ?? existingPart?.risks ?? [],
        riskCardData: part.riskCardData ?? existingPart?.riskCardData,
        riskCardName: part.riskCardName ?? existingPart?.riskCardName,
        riskCardDataRaw: part.riskCardDataRaw ?? existingPart?.riskCardDataRaw,
        riskFeedbackAction: part.riskFeedbackAction ?? existingPart?.riskFeedbackAction,
      };
    }
    
    // Recalculate pricing after updating configuration
    this.currentPartIndex = partIndex;
    this.recalculatePricingForCurrentPart();
    this.emitPartsChange();
  }

  onFileUpload(event: FileUploadEvent): void {
    if (event.success && event.partIndex !== undefined) {
      this.localParts[event.partIndex].files.push(...event.files);
      
      // Save updated data to IndexedDB
      this.saveDataToIndexedDB();
      
      this.validatePart(event.partIndex);
      this.emitPartsChange();
    }
  }

  removeFile(partIndex: number, fileIndex: number): void {
    this.localParts[partIndex].files.splice(fileIndex, 1);
    
    // Save updated data to IndexedDB
    this.saveDataToIndexedDB();
    
    this.validatePart(partIndex);
    this.emitPartsChange();
  }
isShowContinueWithoutFilesMessage(){
  return this.get3DFiles().length ===0 && this.get2DFiles().length === 0;
}
  // ===== FILE TYPE FILTERING METHODS =====

  get3DFiles(): PartFile[] {
    return this.currentPart.files.filter(file => file.fileType === 'Model3D');
  }

  get2DFiles(): PartFile[] {
    return this.currentPart.files.filter(file => file.fileType === 'Drawing2D' || file.fileType.toLowerCase() === 'pdf');
  }

  remove3DFile(filteredIndex: number): void {
    const threeDFiles = this.get3DFiles();
    if (filteredIndex >= 0 && filteredIndex < threeDFiles.length) {
      const fileToRemove = threeDFiles[filteredIndex];
      // Find the actual index in the full files array
      const actualIndex = this.currentPart.files.findIndex(file => file === fileToRemove);
      if (actualIndex >= 0) {
        this.removeFile(this.currentPartIndex, actualIndex);
        // Note: removeFile already calls saveDataToIndexedDB()
      }
    }
  }

  remove2DFile(filteredIndex: number): void {
    const twoDFiles = this.get2DFiles();
    if (filteredIndex >= 0 && filteredIndex < twoDFiles.length) {
      const fileToRemove = twoDFiles[filteredIndex];
      // Find the actual index in the full files array
      const actualIndex = this.currentPart.files.findIndex(file => file === fileToRemove);
      if (actualIndex >= 0) {
        this.removeFile(this.currentPartIndex, actualIndex);
        // Note: removeFile already calls saveDataToIndexedDB()
      }
    }
  }

  // ===== CONFIGURATION METHODS =====

  // Materials Section
  addMaterial(): void {
    if (this.newMaterial.name && this.newMaterial.cost !== null && this.newMaterial.cost > 0) {
      if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
        // Create completely new material object to avoid shared references
        const newMaterialCopy = {
          name: this.newMaterial.name,
          cost: typeof this.newMaterial.cost === 'string' ? parseFloat(this.newMaterial.cost) || 0 : this.newMaterial.cost,
          costType: this.newMaterial.costType,
          reference: this.newMaterial.reference
        };
        
        // Create new array with the new material
        const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials, newMaterialCopy];
        this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;
        
        // Clear form state only after successful addition
        this.newMaterial = { name: '', cost: 0, costType: 'Per Unit', reference: '' };
        this.emitPartsChange();
      }
    }
  }

  // Materials Section - Add new material directly to array
  startNewMaterial(): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const newMaterial = {
        name: '',
        cost: null,
        costType: 'Per Unit' as 'Per Unit' | 'Fixed',
        reference: ''
      };
      
      const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials, newMaterial];
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;
      this.emitPartsChange();
    }
  }

  removeMaterial(index: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      // Create new array without the removed item
      const updatedMaterials = this.localParts[this.currentPartIndex].configuration.materials.filter((_, i) => i !== index);
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
    }
  }

  // Quantity Breakdown Methods
  enableMaterialsQuantityBreakdown(): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      this.localParts[this.currentPartIndex].configuration.materialsQuantityBreakdownEnabled = true;
      // Enable quantity breakdown for all materials and initialize quantity pricing
      this.localParts[this.currentPartIndex].configuration.materials.forEach((material, index) => {
        material.quantityBreakdownEnabled = true;
        if (!material.quantityPricing) {
          material.quantityPricing = {};
        }
      });
      // Create new array to trigger change detection
      const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials];
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;
      // Recalculate pricing after enabling quantity breakdown
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
      this.cdr.detectChanges();
    }
  }

  disableMaterialsQuantityBreakdown(): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      this.localParts[this.currentPartIndex].configuration.materialsQuantityBreakdownEnabled = false;
      // Disable quantity breakdown for all materials
      this.localParts[this.currentPartIndex].configuration.materials.forEach((material) => {
        material.quantityBreakdownEnabled = false;
      });
      // Create new array to trigger change detection
      const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials];
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;
      // Recalculate pricing after disabling quantity breakdown
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
      this.cdr.detectChanges();
    }
  }

  isMaterialsQuantityBreakdownEnabled(): boolean {
    return this.localParts[this.currentPartIndex]?.configuration?.materialsQuantityBreakdownEnabled === true;
  }

  // Per-material quantity breakdown methods
  isMaterialQuantityBreakdownEnabled(materialIndex: number): boolean {
    const material = this.localParts[this.currentPartIndex]?.configuration?.materials[materialIndex];
    return material?.quantityBreakdownEnabled === true;
  }

  /**
   * Check if all materials have quantity breakdown enabled
   */
  areAllMaterialsQuantityBreakdownEnabled(): boolean {
    const materials = this.localParts[this.currentPartIndex]?.configuration?.materials;
    if (!materials || materials.length === 0) {
      return false;
    }
    return materials.every(material => material.quantityBreakdownEnabled === true);
  }

  enableMaterialQuantityBreakdown(materialIndex: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const material = this.localParts[this.currentPartIndex].configuration.materials[materialIndex];
      if (!material) return;

      material.quantityBreakdownEnabled = true;
      if (!material.quantityPricing) {
        material.quantityPricing = {};
      }

      // Create new array to trigger change detection
      const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials];
      updatedMaterials[materialIndex] = { ...material };
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;

      // Recalculate pricing after enabling quantity breakdown
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
      this.cdr.detectChanges();
    }
  }

  disableMaterialQuantityBreakdown(materialIndex: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const material = this.localParts[this.currentPartIndex].configuration.materials[materialIndex];
      if (!material) return;

      material.quantityBreakdownEnabled = false;

      // Create new array to trigger change detection
      const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials];
      updatedMaterials[materialIndex] = { ...material };
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;

      // Recalculate pricing after disabling quantity breakdown
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
      this.cdr.detectChanges();
    }
  }

  // Update material quantity-specific pricing
  updateMaterialQuantityPricing(materialIndex: number, quantity: number, field: 'cost' | 'costType', value: any): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const material = this.localParts[this.currentPartIndex].configuration.materials[materialIndex];
      if (!material) return;

      // Initialize quantityPricing if it doesn't exist
      if (!material.quantityPricing) {
        material.quantityPricing = {};
      }

      const quantityKey = quantity.toString();
      
      // Initialize quantity entry if it doesn't exist
      if (!material.quantityPricing[quantityKey]) {
        material.quantityPricing[quantityKey] = {
          cost: null,
          costType: 'Per Unit'
        };
      }

      // Process value
      let processedValue = value;
      if (field === 'cost') {
        processedValue = typeof value === 'string' ? parseFloat(value) || null : value;
      }

      // Update the quantity-specific pricing
      material.quantityPricing[quantityKey] = {
        ...material.quantityPricing[quantityKey],
        [field]: processedValue
      };

      // Create new array to trigger change detection
      const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials];
      updatedMaterials[materialIndex] = { ...material };
      this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;

      // Clear validation errors when quantity-specific cost is updated
      // If material name exists and all quantity costs are cleared, clear the error
      // If material name exists, clear the error (validation will re-check on submit)
      if (field === 'cost') {
        const hasName = material.name && material.name.trim() !== '';
        const hasAnyQuantityCost = Object.values(material.quantityPricing).some(
          qtyPricing => qtyPricing.cost !== null && qtyPricing.cost !== undefined
        );
        
        // Clear error if material name exists (validation will re-check if cost is still required)
        if (hasName) {
          this.clearSectionFieldError('materials', materialIndex, 'name');
          // Only clear cost error if no quantity costs remain
          if (!hasAnyQuantityCost) {
            this.clearSectionFieldError('materials', materialIndex, 'cost');
          }
        }
      }

      // Recalculate pricing after change
      if (field === 'cost' || field === 'costType') {
        this.recalculatePricingForCurrentPart();
      }

      this.emitPartsChange();
    }
  }

  // Get material cost for a specific quantity
  getMaterialCostForQuantity(material: MaterialItem, quantity: number): number | null {
    if (!material) return null;
    
    // If quantity breakdown is enabled for this material and quantity pricing exists, use it
    if (material.quantityBreakdownEnabled && material.quantityPricing) {
      const quantityKey = quantity.toString();
      const qtyPricing = material.quantityPricing[quantityKey];
      if (qtyPricing && qtyPricing.cost !== null) {
        return qtyPricing.cost;
      }
    }
    
    // Otherwise, use the base cost
    return material.cost;
  }

  // Get material cost type for a specific quantity
  getMaterialCostTypeForQuantity(material: MaterialItem, quantity: number): 'Per Unit' | 'Fixed' {
    if (!material) return 'Per Unit';
    
    // If quantity breakdown is enabled for this material and quantity pricing exists, use it
    if (material.quantityBreakdownEnabled && material.quantityPricing) {
      const quantityKey = quantity.toString();
      const qtyPricing = material.quantityPricing[quantityKey];
      if (qtyPricing && qtyPricing.costType) {
        return qtyPricing.costType;
      }
    }
    
    // Otherwise, use the base cost type
    return material.costType;
  }

  // Get valid quantities for the current part
  getValidQuantities(): number[] {
    const part = this.localParts[this.currentPartIndex];
    if (!part || !part.quantityPricing) return [];
    
    return part.quantityPricing
      .filter(qty => qty.quantity !== null && qty.quantity !== undefined && qty.quantity > 0)
      .map(qty => qty.quantity!)
      .filter((qty, index, self) => self.indexOf(qty) === index); // Remove duplicates
  }

  // Helper methods for ngModel binding with quantity breakdown
  getMaterialQuantityCost(materialIndex: number, quantity: number): number | null {
    const material = this.localParts[this.currentPartIndex]?.configuration?.materials[materialIndex];
    if (!material || !material.quantityPricing) return null;
    return material.quantityPricing[quantity.toString()]?.cost ?? null;
  }

  setMaterialQuantityCost(materialIndex: number, quantity: number, value: number | null): void {
    this.updateMaterialQuantityPricing(materialIndex, quantity, 'cost', value);
  }

  getMaterialQuantityCostType(materialIndex: number, quantity: number): 'Per Unit' | 'Fixed' {
    const material = this.localParts[this.currentPartIndex]?.configuration?.materials[materialIndex];
    if (!material || !material.quantityPricing) return 'Per Unit';
    return material.quantityPricing[quantity.toString()]?.costType ?? 'Per Unit';
  }

  setMaterialQuantityCostType(materialIndex: number, quantity: number, value: 'Per Unit' | 'Fixed'): void {
    this.updateMaterialQuantityPricing(materialIndex, quantity, 'costType', value);
  }

  // Tooling Section - Add new tool directly to array
  startNewTool(): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const newTool = {
        name: '',
        cost: null,
        costType: 'Fixed' as 'Per Unit' | 'Fixed',
        reference: ''
      };
      
      const updatedTools = [...this.localParts[this.currentPartIndex].configuration.tools, newTool];
      this.localParts[this.currentPartIndex].configuration.tools = updatedTools;
      this.emitPartsChange();
    }
  }

  removeTool(index: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      // Create new array without the removed item
      const updatedTools = this.localParts[this.currentPartIndex].configuration.tools.filter((_, i) => i !== index);
      this.localParts[this.currentPartIndex].configuration.tools = updatedTools;
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
    }
  }

  // Processes Section - Add new process directly to array
  startNewProcess(): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const newProcess = {
        name: '',
        machineCost: null,
        setupCost: null,
        machineCostType: 'Per Unit' as 'Per Unit' | 'Fixed',
        setupCostType: 'Fixed' as 'Per Unit' | 'Fixed',
        reference: ''
      };
      
      const updatedProcesses = [...this.localParts[this.currentPartIndex].configuration.processes, newProcess];
      this.localParts[this.currentPartIndex].configuration.processes = updatedProcesses;
      this.emitPartsChange();
    }
  }

  // Additional Costs Section - Add new additional cost directly to array
  startNewAdditionalCost(): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const newAdditionalCost = {
        name: '',
        cost: null,
        costType: 'Fixed' as 'Per Unit' | 'Fixed',
        reference: ''
      };

      const updatedAdditionalCosts = [
        ...this.localParts[this.currentPartIndex].configuration.additionalCosts,
        newAdditionalCost
      ];
      this.localParts[this.currentPartIndex].configuration.additionalCosts = updatedAdditionalCosts;
      this.emitPartsChange();
    }
  }

  removeProcess(index: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      // Create new array without the removed item
      const updatedProcesses = this.localParts[this.currentPartIndex].configuration.processes.filter((_, i) => i !== index);
      this.localParts[this.currentPartIndex].configuration.processes = updatedProcesses;
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
    }
  }

  // Additional Costs
  removeAdditionalCost(index: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      // Create new array without the removed item
      const updatedAdditionalCosts = this.localParts[this.currentPartIndex].configuration.additionalCosts.filter((_, i) => i !== index);
      this.localParts[this.currentPartIndex].configuration.additionalCosts = updatedAdditionalCosts;
      this.recalculatePricingForCurrentPart();
      this.emitPartsChange();
    }
  }

  // Update configuration item values
  updateMaterial = (index: number, field: keyof MaterialItem, value: any): void => {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const currentMaterial = this.localParts[this.currentPartIndex].configuration.materials[index];
      
      // Handle cost field specifically (parse to number)
      let processedValue = value;
      if (field === 'cost') {
        processedValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      }
      
      // Clear validation errors for this field
      if (field === 'name' || field === 'cost') {
        this.clearSectionFieldError('materials', index, field);
      }
      
      // Only update if the value has actually changed
      // if (currentMaterial[field] !== processedValue) {
        // Create new array with updated material
        const updatedMaterials = [...this.localParts[this.currentPartIndex].configuration.materials];
        updatedMaterials[index] = { ...updatedMaterials[index], [field]: processedValue };
        this.localParts[this.currentPartIndex].configuration.materials = updatedMaterials;
        
        // Recalculate pricing after configuration change
        if(field === 'cost' || field === 'costType') {
          this.recalculatePricingForCurrentPart();
        }
        // this.emitPartsChange();
      // }
    }
  }

  updateTool = (index: number, field: keyof ToolItem, value: any): void => {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const currentTool = this.localParts[this.currentPartIndex].configuration.tools[index];
      
      // Handle cost field specifically (parse to number)
      let processedValue = value;
      if (field === 'cost') {
        processedValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      }
      
      // Clear validation errors for this field
      if (field === 'name' || field === 'cost') {
        this.clearSectionFieldError('tools', index, field);
      }
      
      // Only update if the value has actually changed
      // if (currentTool[field] !== processedValue) {
        // Create new array with updated tool
        const updatedTools = [...this.localParts[this.currentPartIndex].configuration.tools];
        updatedTools[index] = { ...updatedTools[index], [field]: processedValue };
        this.localParts[this.currentPartIndex].configuration.tools = updatedTools;
        
        // Recalculate pricing after configuration change
        if(field === 'cost' || field === 'costType') {
          this.recalculatePricingForCurrentPart();
        }
        // this.emitPartsChange();
      // }
    }
  }

  updateProcess = (index: number, field: keyof ProcessItem, value: any): void => {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration.processes) {
      const currentProcess = this.localParts[this.currentPartIndex].configuration.processes[index];
      
      // Handle cost fields specifically (parse to number)
      let processedValue = value;
      if (field === 'machineCost' || field === 'setupCost') {
        processedValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      }
      
      // Clear validation errors for this field
      if (field === 'name' || field === 'machineCost' || field === 'setupCost') {
        if (field === 'name') {
          this.clearSectionFieldError('processes', index, 'name');
        }
        if (field === 'machineCost' || field === 'setupCost') {
          this.clearSectionFieldError('processes', index, 'cost');
        }
      }
      
      // Only update if the value has actually changed
      // if (currentProcess[field] !== processedValue) {
        // Create new array with updated process
        const updatedProcesses = [...this.localParts[this.currentPartIndex].configuration.processes];
        updatedProcesses[index] = { ...updatedProcesses[index], [field]: processedValue };
        this.localParts[this.currentPartIndex].configuration.processes = updatedProcesses;
        
        // Recalculate pricing after configuration change
        if(field === 'machineCost' || field === 'setupCost' || field === 'machineCostType' || field === 'setupCostType') {
        this.recalculatePricingForCurrentPart();
        }
        // this.emitPartsChange();
      // }
    }
  }

  updateAdditionalCost = (index: number, field: keyof AdditionalCostItem, value: any): void => {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].configuration) {
      const currentCost = this.localParts[this.currentPartIndex].configuration.additionalCosts[index];
      
      // Handle cost field specifically (parse to number)
      let processedValue = value;
      if (field === 'cost') {
        processedValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      }
      
      // Clear validation errors for this field
      if (field === 'name' || field === 'cost') {
        this.clearSectionFieldError('additionalCosts', index, field);
      }
      
      // Only update if the value has actually changed
      // if (currentCost[field] !== processedValue) {
        // Create new array with updated additional cost
        const updatedAdditionalCosts = [...this.localParts[this.currentPartIndex].configuration.additionalCosts];
        updatedAdditionalCosts[index] = { ...updatedAdditionalCosts[index], [field]: processedValue };
        this.localParts[this.currentPartIndex].configuration.additionalCosts = updatedAdditionalCosts;
        
        // Recalculate pricing after configuration change
        if(field === 'cost' || field === 'costType') {
          this.recalculatePricingForCurrentPart();
        }
        // this.emitPartsChange();
      // }
    }
  }



  // ===== PRICING CALCULATION METHODS =====

  private toNumber(value: any): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (value === null || value === undefined) return 0;
    const cleaned = typeof value === 'string' ? value.trim() : String(value);
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private recalculatePricingForCurrentPart(): void {
    if (!this.localParts[this.currentPartIndex]) return;
    
    const part = this.localParts[this.currentPartIndex];
    if (!part.quantityPricing || part.quantityPricing.length === 0) return;

    // Calculate base unit cost from configuration (coerced to number inside helpers)
    const baseUnitCost = this.calculateBaseUnitCost();
    
    // Update each pricing tier
    part.quantityPricing.forEach((pricingRow, index) => {
      const quantity = this.toNumber(pricingRow.quantity);
      if (quantity > 0) {
        // Calculate unit cost for this quantity tier
        const unitCost = this.calculateUnitCostForQuantity(baseUnitCost, quantity);
        let unitPrice = 0;
        let total = 0;
        let unitPriceTotal = 0;

        if(unitCost > 0){
          const markup = this.toNumber(pricingRow.markup);
          unitPrice = this.applyMarkup(unitCost, markup);
          
          total = quantity * unitPrice;
          unitPriceTotal = quantity * unitPrice;
        }
        // Update the pricing row
        const updatedPricing = [...part.quantityPricing];
        updatedPricing[index] = {
          ...pricingRow,
          unitCost: this.roundToTwoDecimals(unitCost),
          unitPrice: this.roundToTwoDecimals(unitPrice),
          total: this.roundToTwoDecimals(total),
          unitPriceTotal: this.roundToTwoDecimals(unitPriceTotal)
        };
        part.quantityPricing = updatedPricing;

        this.hasValidTotalForCurrentPart()
      }
    });
  }

  private calculateBaseUnitCost(): number {
    const part = this.localParts[this.currentPartIndex];
    if (!part.configuration) return 0;

    let totalUnitCost = 0;

    // Calculate materials cost (Per Unit costs)
    const materialsCost = part.configuration.materials
      .filter(material => material.costType === 'Per Unit')
      .reduce((sum, material) => sum + this.toNumber(material.cost), 0);

    // Calculate materials cost (Fixed costs - will be distributed per unit)
    const materialsFixedCost = part.configuration.materials
      .filter(material => material.costType === 'Fixed')
      .reduce((sum, material) => sum + this.toNumber(material.cost), 0);

    // Calculate tools cost (Per Unit costs)
    const toolsPerUnitCost = part.configuration.tools
      .filter(tool => tool.costType === 'Per Unit')
      .reduce((sum, tool) => sum + this.toNumber(tool.cost), 0);

    // Calculate tools cost (Fixed costs - will be distributed per unit)
    const toolsFixedCost = part.configuration.tools
      .filter(tool => tool.costType === 'Fixed')
      .reduce((sum, tool) => sum + this.toNumber(tool.cost), 0);

    // Calculate processes cost (Per Unit costs)
    const processesPerUnitCost = part.configuration.processes.reduce((sum, process) => {
      let processCost = 0;
      if (process.machineCostType === 'Per Unit') {
        processCost += this.toNumber(process.machineCost);
      }
      if (process.setupCostType === 'Per Unit') {
        processCost += this.toNumber(process.setupCost);
      }
      return sum + processCost;
    }, 0);

    // Calculate processes cost (Fixed costs)
    const processesFixedCost = part.configuration.processes.reduce((sum, process) => {
      let processCost = 0;
      if (process.machineCostType === 'Fixed') {
        processCost += this.toNumber(process.machineCost);
      }
      if (process.setupCostType === 'Fixed') {
        processCost += this.toNumber(process.setupCost);
      }
      return sum + processCost;
    }, 0);

    // Calculate additional costs (Per Unit)
    const additionalPerUnitCosts = part.configuration.additionalCosts.reduce((sum, cost) => {
      if (cost.costType === 'Per Unit') {
        return sum + this.toNumber(cost.cost);
      }
      return sum;
    }, 0);

    // Calculate additional costs (Fixed)
    const additionalFixedCosts = part.configuration.additionalCosts.reduce((sum, cost) => {
      if (cost.costType === 'Fixed') {
        return sum + this.toNumber(cost.cost);
      }
      return sum;
    }, 0);

    // Sum up all per-unit costs
    totalUnitCost = materialsCost + toolsPerUnitCost + processesPerUnitCost + additionalPerUnitCosts;

    // Add fixed costs (they will be distributed per unit when quantity is known)
    const fixedCosts = materialsFixedCost + toolsFixedCost + processesFixedCost + additionalFixedCosts;
    
    return this.toNumber(totalUnitCost + fixedCosts);
  }

  private calculateUnitCostForQuantity(baseUnitCost: number, quantity: number): number {
    const qty = this.toNumber(quantity);
    if (qty <= 0) return 0;
    
    const part = this.localParts[this.currentPartIndex];
    if (!part.configuration) return this.toNumber(baseUnitCost);

    // Calculate per-unit costs (these don't change with quantity)
    let perUnitCosts = 0;
    
    // Use getMaterialCostForQuantity which handles both quantity breakdown and standard pricing per material
    part.configuration.materials.forEach(material => {
      const materialCost = this.getMaterialCostForQuantity(material, qty);
      const materialCostType = this.getMaterialCostTypeForQuantity(material, qty);
      
      if (materialCost !== null) {
        if (materialCostType === 'Per Unit') {
          perUnitCosts += this.toNumber(materialCost);
        } else {
          // Fixed cost - divide by quantity to get per unit cost
          perUnitCosts += this.toNumber(materialCost) / qty;
        }
      }
    });

    // Tools (Per Unit)
    perUnitCosts += part.configuration.tools
      .filter(tool => tool.costType === 'Per Unit')
      .reduce((sum, tool) => sum + this.toNumber(tool.cost), 0);

    // Tools (Fixed) - divide by quantity to get per unit cost
    perUnitCosts += part.configuration.tools
      .filter(tool => tool.costType === 'Fixed')
      .reduce((sum, tool) => sum + (this.toNumber(tool.cost) / qty), 0);

    // Processes (Per Unit machine costs)
    perUnitCosts += part.configuration.processes
      .filter(process => process.machineCostType === 'Per Unit')
      .reduce((sum, process) => sum + this.toNumber(process.machineCost), 0);

    // Processes (Fixed machine costs) - divide by quantity to get per unit cost
    perUnitCosts += part.configuration.processes
      .filter(process => process.machineCostType === 'Fixed')
      .reduce((sum, process) => sum + (this.toNumber(process.machineCost) / qty), 0);

    // Processes (Per Unit setup costs)
    perUnitCosts += part.configuration.processes
      .filter(process => process.setupCostType === 'Per Unit')
      .reduce((sum, process) => sum + this.toNumber(process.setupCost), 0);

    // Processes (Fixed setup costs) - divide by quantity to get per unit cost
    perUnitCosts += part.configuration.processes
      .filter(process => process.setupCostType === 'Fixed')
      .reduce((sum, process) => sum + (this.toNumber(process.setupCost) / qty), 0);

    // Additional costs (Per Unit)
    perUnitCosts += part.configuration.additionalCosts
      .filter(cost => cost.costType === 'Per Unit')
      .reduce((sum, cost) => sum + this.toNumber(cost.cost), 0);

    // Additional costs (Fixed) - divide by quantity to get per unit cost
    perUnitCosts += part.configuration.additionalCosts
      .filter(cost => cost.costType === 'Fixed')
      .reduce((sum, cost) => sum + (this.toNumber(cost.cost) / qty), 0);

    // All costs are now handled in perUnitCosts section above
    return this.toNumber(perUnitCosts);
  }

  private applyMarkup(unitCost: number, markupPercentage: number): number {
    const cost = this.toNumber(unitCost);
    const markup = this.toNumber(markupPercentage);
    if (markup <= 0) return cost;
    return cost * (1 + markup / 100);
  }

  private roundToTwoDecimals(value: number): number {
    const num = this.toNumber(value);
    return Math.round(num * 100) / 100;
  }

  // Pricing Table - Update pricing row values
  updatePricingRow = (index: number, field: keyof QuantityPricing, value: any): void => {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].quantityPricing) {
      const currentRow = this.localParts[this.currentPartIndex].quantityPricing[index];
      
      // Handle numeric fields specifically (parse to number)
      let processedValue = value;
      if (field === 'quantity' || field === 'unitCost' || field === 'markup' || field === 'unitPrice' || field === 'leadtimeValue') {
        processedValue = this.toNumber(value);
      }
      
      // Only update if the value has actually changed
      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = { ...updatedPricing[index], [field]: processedValue } as any;
      
      // If leadtimeValue or leadtimeUnit was updated, hide the input fields
      if ((field === 'leadtimeValue' && processedValue !== null && processedValue !== 0) || field === 'leadtimeUnit') {
        updatedPricing[index] = { ...updatedPricing[index], showLeadTimeInputFields: false } as any;
        
        // Clear LeadTime validation error when user enters a valid value
        if (field === 'leadtimeValue' && processedValue !== null && processedValue > 0) {
          this.clearLeadTimeValidationError(index);
        }
      }
      
      // If quantity was updated, update the editing permissions for MFG integration
      if (field === 'quantity') {
        const isValidQuantity = processedValue !== null && processedValue !== undefined;
        updatedPricing[index] = { 
          ...updatedPricing[index], 
          isValidQuantity: isValidQuantity,
          isQuantityEditable: this.integrationType !== 'MFG' || isValidQuantity
        } as any;
        
        // Clear LeadTime validation error when quantity is updated (might affect LeadTime requirement)
        this.clearLeadTimeValidationError(index);
        
        // Clear quantity validation error if at least one pricing now has quantity > 0
        if (this.hasValidPricingForCurrentPart('quantity')) {
          this.clearValidationError('quantity');
        }
      }
      
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;
      
      // Call field-specific calculation methods
      if (field === 'unitCost') {
        this.onUnitCostChange(index);
      } else if (field === 'unitPrice') {
        this.onUnitPriceChange(index);
      } else if (field === 'markup') {
        this.onMarkupChange(index);
      } else if (field === 'quantity') {
        this.onQuantityChange(index);
      } else if (field === 'total') {
        this.onTotalChange(index);
      } else {
        // For other fields (leadtime), just recalculate total
        this.recalculatePricingTotal(index);
      }
      
      this.emitPartsChange();
    }
  }

  // Calculate reverse pricing when total is changed by user
  private calculateReversePricing(index: number): void {
    if (!this.localParts[this.currentPartIndex] || !this.localParts[this.currentPartIndex].quantityPricing) {
      return;
    }

    const row = this.localParts[this.currentPartIndex].quantityPricing[index];
    const quantity = this.toNumber(row.quantity);
    const total = this.toNumber(row.total);
    const unitCost = this.toNumber(row.unitCost);

    // Only proceed if we have valid quantity, total, and unitCost
    if (quantity <= 0 || total <= 0) {
      return;
    }

    // Calculate unit price from total and quantity
    const unitPrice = total / quantity;
    
    // Calculate markup percentage from unit cost and unit price
    // Formula: markup = ((unitPrice / unitCost) - 1) * 100
    let markup = 0;
    if(unitCost > 0) {
      markup = ((unitPrice / unitCost) - 1) * 100;
    }

    // Update the pricing row with calculated values
    const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
    updatedPricing[index] = {
      ...row,
      unitPrice: this.roundToTwoDecimals(unitPrice),
      markup: this.roundToTwoDecimals(markup),
      unitPriceTotal: this.roundToTwoDecimals(unitPrice)
    };
    
    this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;
  }

  // Start editing leadtime for a specific row
  startEditingLeadtime = (row: QuantityPricing, index: number): void => {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].quantityPricing) {
      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = { 
        ...updatedPricing[index], 
        showLeadTimeInputFields: true 
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;
      
      // Clear LeadTime validation error when user starts editing
      const prefix = `part${this.currentPartIndex}`;
      delete this.validationErrors[`${prefix}_leadtime`];
      
      // Clear section validation error for LeadTime
      this.clearLeadTimeValidationError(index);
      
      // Force change detection to show the input field
      this.cdr.detectChanges();
      
      // Focus on the input field after it appears
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-leadtime-index="${index}"]`) as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }

  // Start editing quantity for a specific row (for non-MFG integrations)
  startEditingQuantity = (row: QuantityPricing, index: number): void => {
    if (this.integrationType === 'MFG') {
      return; // Don't allow editing invalid quantities in MFG mode
    }
    
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].quantityPricing) {
      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = { 
        ...updatedPricing[index], 
        isQuantityEditable: true,
        isValidQuantity: false // Will be set to true when user enters a value
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;
      
      // Force change detection to show the input field
      this.cdr.detectChanges();
      
      // Focus on the input field after it appears
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-quantity-index="${index}"]`) as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }

  // Handle blur event on leadtime input - exit editing mode if no value entered
  onLeadtimeInputBlur = (row: QuantityPricing, index: number): void => {
    // Small delay to allow change event to fire first
    setTimeout(() => {
      if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].quantityPricing) {
        const currentRow = this.localParts[this.currentPartIndex].quantityPricing[index];
        
        // If no value was entered, exit editing mode
        if (!currentRow.leadtimeValue || currentRow.leadtimeValue === 0) {
          const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
          updatedPricing[index] = { ...updatedPricing[index], showLeadTimeInputFields: false } as any;
          this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;
          
          // Force change detection
          this.cdr.detectChanges();
        }
      }
    }, 150);
  }

  private recalculatePricingTotal(index: number): void {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].quantityPricing) {
      const row = this.localParts[this.currentPartIndex].quantityPricing[index];
      const quantity = this.toNumber(row.quantity);
      const unitPrice = this.toNumber(row.unitPrice);
      const total = quantity * unitPrice;
      const unitPriceTotal = quantity * unitPrice;
      
      // Create new array with updated total and unitPriceTotal
      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = { ...updatedPricing[index], total, unitPriceTotal } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;

      // Clear total validation error if at least one pricing now has total > 0
      this.hasValidTotalForCurrentPart()
    }
  }

  // Field-specific calculation methods based on requirements

  private onUnitCostChange(index: number): void {
    if (!this.localParts[this.currentPartIndex] || !this.localParts[this.currentPartIndex].quantityPricing) {
      return;
    }

    const row = this.localParts[this.currentPartIndex].quantityPricing[index];
    const quantity = this.toNumber(row.quantity);
    const unitCost = this.toNumber(row.unitCost);
    const markup = this.toNumber(row.markup);

    if (quantity && unitCost) {
      let unitPrice: number;
      
      if (markup === 0) {
        unitPrice = unitCost;
      } else {
        unitPrice = unitCost * (1 + markup / 100);
      }

      const total = unitPrice * quantity;
      const unitPriceTotal = unitPrice * quantity;

      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = {
        ...updatedPricing[index],
        unitPrice: this.roundToTwoDecimals(unitPrice),
        total: this.roundToTwoDecimals(total),
        unitPriceTotal: this.roundToTwoDecimals(unitPriceTotal)
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;

      // Clear total validation error if at least one pricing now has total > 0
      this.hasValidTotalForCurrentPart();
    }
  }

  private onUnitPriceChange(index: number): void {
    if (!this.localParts[this.currentPartIndex] || !this.localParts[this.currentPartIndex].quantityPricing) {
      return;
    }

    const row = this.localParts[this.currentPartIndex].quantityPricing[index];
    const quantity = this.toNumber(row.quantity);
    let unitPrice = this.toNumber(row.unitPrice);
    let unitCost = this.toNumber(row.unitCost);
    let markup = this.toNumber(row.markup);
    let unitPriceTotal = 0;
    let total = 0;

    if(unitPrice === 0){
      unitPrice = unitCost;
    }else{
      if (quantity && unitPrice) {
        // Handle zero UnitCost case
        if (unitCost === 0) {
          unitCost = unitPrice;
          // markup = 0;
        }
        // Calculate markup
        if (unitCost !== 0) {
          markup = ((unitPrice - unitCost) / unitCost) * 100;
        } else {
          markup = 0;
        }
      }
    }
    total = unitPrice * quantity;
    unitPriceTotal = unitPrice * quantity;
    const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = {
        ...updatedPricing[index],
        unitPrice: this.roundToTwoDecimals(unitPrice),
        unitCost: this.roundToTwoDecimals(unitCost),
        markup: this.roundToTwoDecimals(markup),
        total: this.roundToTwoDecimals(total),
        unitPriceTotal: this.roundToTwoDecimals(unitPriceTotal)
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;

      // Clear total validation error if at least one pricing now has total > 0
      this.hasValidTotalForCurrentPart();
  }

  private onMarkupChange(index: number): void {
    if (!this.localParts[this.currentPartIndex] || !this.localParts[this.currentPartIndex].quantityPricing) {
      return;
    }

    const row = this.localParts[this.currentPartIndex].quantityPricing[index];
    const quantity = this.toNumber(row.quantity);
    let unitPrice = this.toNumber(row.unitPrice);
    let unitCost = this.toNumber(row.unitCost);
    const markup = this.toNumber(row.markup);

    if (quantity && (unitCost || unitPrice)) {
      // Handle zero cases
      if (unitPrice === 0 && unitCost === 0) {
        return;
      }

      if (unitPrice === 0) {
        unitPrice = unitCost * (1 + markup / 100);
      }

      if (unitCost === 0) {
        unitCost = unitPrice / (1 + markup / 100);
      }

      // Recalculate UnitPrice with new markup
      unitPrice = unitCost * (1 + markup / 100);
      const total = unitPrice * quantity;
      const unitPriceTotal = unitPrice * quantity;

      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = {
        ...updatedPricing[index],
        unitPrice: this.roundToTwoDecimals(unitPrice),
        unitCost: this.roundToTwoDecimals(unitCost),
        total: this.roundToTwoDecimals(total),
        unitPriceTotal: this.roundToTwoDecimals(unitPriceTotal)
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;

      // Clear total validation error if at least one pricing now has total > 0
      this.hasValidTotalForCurrentPart();
    }
  }

  private onQuantityChange(index: number): void {
    if (!this.localParts[this.currentPartIndex] || !this.localParts[this.currentPartIndex].quantityPricing) {
      return;
    }

    const row = this.localParts[this.currentPartIndex].quantityPricing[index];
    const quantity = this.toNumber(row.quantity);

    if (quantity > 0) {
      const baseUnitCost = this.calculateBaseUnitCost();
      let unitCost = this.calculateUnitCostForQuantity(baseUnitCost, quantity);
      
      const markup = this.toNumber(row.markup);
      let unitPrice: number = this.toNumber(row.unitPrice);
      if(unitCost > 0){
        if (markup === 0) {
          unitPrice = unitCost;
        } else {
          unitPrice = unitCost * (1 + markup / 100);
        }
      } else{
        unitCost = unitPrice;
      }

      const total = unitPrice * quantity;
      const unitPriceTotal = unitPrice * quantity;

      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = {
        ...updatedPricing[index],
        unitCost: this.roundToTwoDecimals(unitCost),
        unitPrice: this.roundToTwoDecimals(unitPrice),
        total: this.roundToTwoDecimals(total),
        unitPriceTotal: this.roundToTwoDecimals(unitPriceTotal)
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;

      // Clear total validation error if at least one pricing now has total > 0
      this.hasValidTotalForCurrentPart();
    }
  }

  private onTotalChange(index: number): void {
    if (!this.localParts[this.currentPartIndex] || !this.localParts[this.currentPartIndex].quantityPricing) {
      return;
    }

    const row = this.localParts[this.currentPartIndex].quantityPricing[index];
    const quantity = this.toNumber(row.quantity);
    const total = this.toNumber(row.total);

    if (quantity && total) {
      const unitPrice = total / quantity;

      // Update the pricing row first
      const updatedPricing = [...this.localParts[this.currentPartIndex].quantityPricing];
      updatedPricing[index] = {
        ...updatedPricing[index],
        unitPrice: this.roundToTwoDecimals(unitPrice),
        unitPriceTotal: this.roundToTwoDecimals(unitPrice)
      } as any;
      this.localParts[this.currentPartIndex].quantityPricing = updatedPricing;

      // Trigger UnitPrice change logic
      this.onUnitPriceChange(index);
    }

    // Clear total validation error if at least one pricing now has total > 0
    this.hasValidTotalForCurrentPart();
  }

  calculateTotal(): number {
    if (this.localParts[this.currentPartIndex] && this.localParts[this.currentPartIndex].quantityPricing) {
      return this.localParts[this.currentPartIndex].quantityPricing.reduce((sum, row) => sum + this.toNumber(row.total), 0);
    }
    return 0;
  }

  // Section toggles
  toggleSection(section: string): void {
    switch (section) {
      case 'materials':
        this.showMaterials = !this.showMaterials;
        break;
      case 'tooling':
        this.showTooling = !this.showTooling;
        break;
      case 'processes':
        this.showProcesses = !this.showProcesses;
        break;
      case 'additionalCosts':
        this.showAdditionalCosts = !this.showAdditionalCosts;
        break;
    }
  }

  confirmPartPricing(): void {
    this.enableLeadTimeValidation();
    
    // Run all validations at once
    const isCurrentPartValid = this.validateCurrentPart();
    const isSectionInputsValid = this.validateSectionInputs();
    
    // If any validation fails, show all errors and return
    if (!isCurrentPartValid || !isSectionInputsValid) {
      if (!isCurrentPartValid) {
        this.emitValidationErrors();
      }
      this.cdr.detectChanges();
      return;
    }
    
    if (this.localParts[this.currentPartIndex]) {
      this.localParts = [...this.localParts];
      this.localParts[this.currentPartIndex].isPartDetailCompleted = true;
      
      this.clearLeadTimeValidationErrors();
      const mixPanelEvent = this.getMixPanelEventForIntegration(this.integrationType);
      this.addTracking(mixPanelEvent, this.queryParams);
      this.emitPartsChange();
      
      this.cdr.detectChanges();
      
      console.log(`[PartsInformation] Part ${this.currentPartIndex} marked as completed:`, this.localParts[this.currentPartIndex]);
    } else {
      console.error(`[PartsInformation] No part found at index ${this.currentPartIndex}`);
    }
    this.emitPartsChange();
    
    // Check if survey should be shown before navigation
    if (this.surveyDone || PartsInformationComponent.surveyShownInSession) {
      // Survey already completed or shown in session, proceed with navigation
      this.stepNavigation.emit({
        fromStep: 'parts-information',
        toStep: 'costs',
        canProceed: true,
        partsPricingData: this.localParts,
        isConfirmQuote: true
      });
    } else {
      // Show feedback survey first
      this.pendingNavigation = true;
      PartsInformationComponent.surveyShownInSession = true; // Mark survey as shown in session
      this.showFeedbackSurveyModal();
    }
  }

  // ===== FEEDBACK SURVEY METHODS =====
  
  /**
   * Show feedback survey after successful part pricing confirmation
   */
  showFeedbackSurveyModal(): void {
    this.showFeedbackSurvey = true;
    this.feedbackStep = 'rating';
    this.feedbackRating = null;
    this.feedbackComment = '';
    this.cdr.detectChanges();
  }

  /**
   * Handle feedback rating selection
   */
  onFeedbackRatingSelect(rating: 'positive' | 'negative'): void {
    this.feedbackRating = rating;
    this.feedbackStep = 'comment';
    this.cdr.detectChanges();
  }

  /**
   * Handle feedback survey back navigation
   */
  onFeedbackBack(): void {
    if (this.feedbackStep === 'comment') {
      this.feedbackStep = 'rating';
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle feedback survey close/skip
   */
  onFeedbackClose(): void {
    this.showFeedbackSurvey = false;
    this.resetFeedbackState();
    this.handlePendingNavigation();
  }

  /**
   * Submit feedback and close survey
   */
  onFeedbackSubmit(): void {
    // if (!this.feedbackComment.trim()) {
    //   this.toastrService.error('Please provide your feedback');
    //   return;
    // }

    // Show loading state
    this.isSubmittingFeedback = true;
    this.cdr.detectChanges();

    // Prepare feedback request with proper data mapping
    const feedbackRequest: SaveFeedbackRequest = {
      rfqId: this.quoteData?.rfqId || '',
      companyId: this.companyId?.toString() || '',
      contactId: this.contactId || 0,
      surveyFeedback: this.feedbackRating === 'positive' ? 'Like' : 'Dislike',
      surveyComments: this.feedbackComment.trim()
    };

    // Submit feedback to API
    this.quoteAssistV2Service.saveFeedback(feedbackRequest).subscribe({
      next: (response: SaveFeedbackResponse) => {
        // Hide loading state
        this.isSubmittingFeedback = false;
        
        // Log successful feedback submission
        this.logEvent('Feedback Survey Submitted', {
          rating: this.feedbackRating,
          comment: this.feedbackComment.trim(),
          rfqId: this.quoteData?.rfqId,
          integrationType: this.integrationType,
          apiResponse: response
        });

        // Close feedback survey and proceed with navigation
        this.showFeedbackSurvey = false;
        this.resetFeedbackState();
        this.handlePendingNavigation();
      },
      error: (error: any) => {
        // Hide loading state
        this.isSubmittingFeedback = false;
        
        // Show error message and proceed with navigation
        this.toastrService.error('Feedback not saved');
        
        // Log the error for debugging
        this.logEvent('Feedback Survey Error', {
          rating: this.feedbackRating,
          comment: this.feedbackComment.trim(),
          rfqId: this.quoteData?.rfqId,
          error: error
        });

        // Close feedback survey and proceed with navigation
        this.showFeedbackSurvey = false;
        this.resetFeedbackState();
        this.handlePendingNavigation();
      }
    });
  }

  /**
   * Reset feedback survey state
   */
  private resetFeedbackState(): void {
    this.feedbackStep = 'rating';
    this.feedbackRating = null;
    this.feedbackComment = '';
    this.isSubmittingFeedback = false;
  }

  /**
   * Handle pending navigation after feedback survey completion
   */
  private handlePendingNavigation(): void {
    if (this.pendingNavigation) {
      this.pendingNavigation = false;
      
      // Navigate to next step
      this.stepNavigation.emit({
        fromStep: 'parts-information',
        toStep: 'costs',
        canProceed: true,
        partsPricingData: this.localParts,
        isConfirmQuote: true
      });
    }
  }

  /**
   * Reset survey session flag (for debugging/testing purposes)
   * This is a static method so it can be called from anywhere
   */
  static resetSurveySessionFlag(): void {
    PartsInformationComponent.surveyShownInSession = false;
  }

  // ===== VALIDATION =====

  private validateAllParts(): boolean {
    if(this.integrationType === 'MFGDirect'){
      this.validationErrors = {};
      let returnValue = true;
      this.localParts.forEach((part, index) => {
        const isValid = this.validatePart(index);
        if(!isValid){
          returnValue = false;
        }
      });
      return returnValue;
    }else{
      return true;
    }
  }

  private validateSectionInputs(): boolean {
    this.sectionValidationErrors = {};
    let isValid = true;
    const currentPart = this.localParts[this.currentPartIndex];
    
    if (!currentPart || !currentPart.configuration) {
      return true;
    }

    // Validate Materials section
    if (currentPart.configuration.materials) {
      this.sectionValidationErrors['materials'] = {};
      currentPart.configuration.materials.forEach((material, index) => {
        const hasName = material.name && material.name.trim() !== '';
        const hasBaseCost = material.cost !== null && material.cost !== undefined;
        
        // Check for quantity-specific costs if quantity breakdown is enabled
        let hasQuantitySpecificCost = false;
        if (material.quantityBreakdownEnabled && material.quantityPricing) {
          hasQuantitySpecificCost = Object.values(material.quantityPricing).some(
            qtyPricing => qtyPricing.cost !== null && qtyPricing.cost !== undefined
          );
        }
        
        // Validate: if base cost exists, name must exist
        // OR if any quantity-specific cost exists, name must exist
        if ((!hasName && hasBaseCost) || (!hasName && hasQuantitySpecificCost)) {
          this.sectionValidationErrors['materials'][index] = { name: !hasName, cost: !hasName && (hasBaseCost || hasQuantitySpecificCost) };
          isValid = false;
        }
      });
    }

    // Validate Tools section (not tooling)
    if (currentPart.configuration.tools) {
      this.sectionValidationErrors['tools'] = {};
      currentPart.configuration.tools.forEach((tool, index) => {
        const hasName = tool.name && tool.name.trim() !== '';
        const hasCost = tool.cost !== null && tool.cost !== undefined;
        
        if ((!hasName && hasCost)) {
          this.sectionValidationErrors['tools'][index] = { name: !hasName, cost: !hasCost };
          isValid = false;
        }
      });
    }

    // Validate Processes section
    if (currentPart.configuration.processes) {
      this.sectionValidationErrors['processes'] = {};
      currentPart.configuration.processes.forEach((process, index) => {
        const hasName = process.name && process.name.trim() !== '';
        const hasMachineCost = process.machineCost !== null && process.machineCost !== undefined && process.machineCost > 0;
        const hasSetupCost = process.setupCost !== null && process.setupCost !== undefined && process.setupCost > 0;
        
        // if ((hasName && !hasMachineCost && !hasSetupCost) || (!hasName && (hasMachineCost || hasSetupCost))) {
        if ((!hasName && (hasMachineCost || hasSetupCost))) {
          this.sectionValidationErrors['processes'][index] = { 
            name: !hasName, 
            cost: !hasMachineCost && !hasSetupCost 
          };
          isValid = false;
        }
      });
    }

    // Validate Additional Costs section
    if (currentPart.configuration.additionalCosts) {
      this.sectionValidationErrors['additionalCosts'] = {};
      currentPart.configuration.additionalCosts.forEach((cost, index) => {
        const hasName = cost.name && cost.name.trim() !== '';
        const hasCost = cost.cost !== null && cost.cost !== undefined;
        
        if ((!hasName && hasCost)) {
          this.sectionValidationErrors['additionalCosts'][index] = { name: !hasName, cost: !hasCost };
          isValid = false;
        }
      });
    }

    // Validate LeadTime section - if quantity is entered, LeadTime must also be entered
    if (currentPart.quantityPricing) {
      this.sectionValidationErrors['leadtime'] = {};
      currentPart.quantityPricing.forEach((pricing, index) => {
        const hasQuantity = pricing.quantity !== null && pricing.quantity !== undefined && pricing.quantity > 0;
        const hasLeadTime = pricing.leadtimeValue !== null && pricing.leadtimeValue !== undefined && pricing.leadtimeValue > 0;
        
        if (hasQuantity && !hasLeadTime) {
          this.sectionValidationErrors['leadtime'][index] = { name: false, cost: true };
          isValid = false;
        }
      });
    }

    return isValid;
  }

  private validatePart(partIndex: number): boolean {
    const part = this.localParts[partIndex];
    const prefix = `part${partIndex}`;
    part.isValid = false;
    part.isValidated = true;
    
    // Validate part name
    if (!part.partName?.trim()) {
      this.validationErrors[`${prefix}_name`] = 'Required';
    } else if (part.partName.trim().length < 2) {
      this.validationErrors[`${prefix}_name`] = 'Minimum 2 characters';
    } else if (part.partName.trim().length > 100) {
      this.validationErrors[`${prefix}_name`] = 'Maximum 100 characters';
    } else {
      delete this.validationErrors[`${prefix}_name`];
    }

    // Validate part number
    if (!part.partNumber?.trim()) {
      this.validationErrors[`${prefix}_number`] = 'Required';
    } else if (part.partNumber.trim().length < 2) {
      this.validationErrors[`${prefix}_number`] = 'Minimum 2 characters';
    } else if (part.partNumber.trim().length > 10) {
      this.validationErrors[`${prefix}_number`] = 'Maximum 10 characters';
    } else {
      delete this.validationErrors[`${prefix}_number`];
    }
    
    // Validate continue without files
    if (!part.isContinueWithoutFiles && part.files.length === 0) {
      this.validationErrors[`${prefix}_continueWithoutFiles`] = 'Required';
    } else {
      part.isValid = true;
      delete this.validationErrors[`${prefix}_continueWithoutFiles`];
    }

    if(!part.isValid){
     return false;
    }

    // Validate LeadTime when Part Quantity is present - only if validation is explicitly requested
    if (this.shouldValidateLeadTime && part.quantityPricing && part.quantityPricing.length > 0) {
      let hasLeadTimeError = false;
      
      for (let i = 0; i < part.quantityPricing.length; i++) {
        const pricing = part.quantityPricing[i];
        if (pricing.quantity && pricing.quantity > 0) {
          if (!pricing.leadtimeValue || pricing.leadtimeValue <= 0) {
            hasLeadTimeError = true;
            break; 
          }
        }
      }
      
      if (hasLeadTimeError) {
        this.validationErrors[`${prefix}_leadtime`] = 'Required';
      } else {
        delete this.validationErrors[`${prefix}_leadtime`];
      }
    }
    return true;
  }

  validate(): boolean {
    this.validateAllParts();
    return Object.keys(this.validationErrors).length === 0;
  }

  // ===== UTILITY METHODS =====

  private emitPartsChange(): void {
    const isValid = this.isFormValid();
    const stepCompleted = this.isStepCompleted();

    const event: PartsChangeEvent = {
      parts: [...this.localParts],
      isValid,
      errors: { ...this.validationErrors },
      stepCompleted
    };

    this.partsChange.emit(event);
  }

  private emitValidationErrors(): void {
    // Emit validation errors through parts change event so parent can update progress-tracker
    this.emitPartsChange();
  }

  private isFormValid(): boolean {
    return Object.keys(this.validationErrors).length === 0;
  }

  private isStepCompleted(): boolean {
    // Check if at least one part is complete and valid
    return this.localParts.some((part, index) => {
      const prefix = `part${index}`;
      const hasBasicValidation = part.partName?.trim() && 
                                part.partNumber?.trim() && 
                                !this.validationErrors[`${prefix}_name`] &&
                                !this.validationErrors[`${prefix}_number`];
      
      // Only check LeadTime validation if it's been explicitly requested
      if (this.shouldValidateLeadTime) {
        return hasBasicValidation && !this.validationErrors[`${prefix}_leadtime`];
      }
      
      return hasBasicValidation;
    });
  }

  private logEvent(eventName: string, data: unknown): void {
    console.log(`[PartsInformation] ${eventName}:`, data);
  }

  /**
   * Clear all LeadTime validation errors
   */
  private clearLeadTimeValidationErrors(): void {
    this.localParts.forEach((part, index) => {
      const prefix = `part${index}`;
      delete this.validationErrors[`${prefix}_leadtime`];
    });
    
    // Also clear section validation errors for LeadTime
    if (this.sectionValidationErrors['leadtime']) {
      this.sectionValidationErrors['leadtime'] = {};
    }
  }



  /**
   * Disable LeadTime validation and clear related errors
   */
  private disableLeadTimeValidation(): void {
    this.shouldValidateLeadTime = false;
    this.clearLeadTimeValidationErrors();
  }

  /**
   * Public method to reset LeadTime validation state
   * Call this when you want to clear validation errors and start fresh
   */
  public resetLeadTimeValidation(): void {
    this.disableLeadTimeValidation();
  }

  /**
   * Public method to enable LeadTime validation
   * Call this when you want to start validating LeadTime requirements
   */
  public enableLeadTimeValidation(): void {
    this.shouldValidateLeadTime = true;
  }

  /**
   * Set metadata for MFGDirect integration with combined supplier and extracted content
   * @param extractedContent The extracted content from PDF
   */
  private setMetadataForMfgDirect(extractedContent: string): void {
    this.currentPart.extractedContent = extractedContent;
    // Hardcoded supplier information (same as V1)
    const supplierDetails = {
      "employee_size": "3-9",
      "my_capabilities": ["Machining", "3D Printing", "Engineering & Design Services", "Electro-Mechanical Assembly"],
      "my_equipment": ["24\" Finger Break", "2X SLA 3D Printer", "2X UT 6060 CNC mill 24", "000 RPM Spindle", "8\" Pedestal Grinder", "Automatic Band Saw", "Bridgeport TC4 CNC Mill", "FDM 3D Printer", "Haas VF5 30", "000 RPM Spindle", "Laser Welder/ Cutter / Cleaner", "Okuma Genos L400 Lathe"],
      "industry_serving": ["Job Shop / Contract Manufacturer"],
      "my_address": "23700 Research Dr, Farmington Hills, Michigan, 48335, United States"
    };

    // Combine supplier details with extracted content (same structure as V1)
    const combinedData = {
      ...supplierDetails,
      extractedContent: extractedContent
    };

    // Set metadata in service
    this.quoteAssistV2Service.setMetadata(JSON.stringify(combinedData));
  }

  /**
   * Start quote assist process for the current part
   */
  startQuoteAssist(skipValidation: boolean = false): void {
    if(this.currentPart.isAiDataLoaded){
      return;
    }
    if (!skipValidation) {
      const hasValidName = !!this.currentPart && !!this.currentPart.partName && this.currentPart.partName.trim().length > 0;
      const hasValidNumber = !!this.currentPart && !!this.currentPart.partNumber && this.currentPart.partNumber.trim().length > 0;
      
      // Check LeadTime validation when Part Quantity is present - only if validation is explicitly requested
      let hasValidLeadTime = true;
      if (this.shouldValidateLeadTime && this.currentPart.quantityPricing && this.currentPart.quantityPricing.length > 0) {
        for (const pricing of this.currentPart.quantityPricing) {
          if (pricing.quantity && pricing.quantity > 0) {
            if (!pricing.leadtimeValue || pricing.leadtimeValue <= 0) {
              hasValidLeadTime = false;
              break;
            }
          }
        }
      }
      
      // For MFG integration, ensure part name and number are populated from API
      if (this.integrationType === 'MFG' && (!hasValidName || !hasValidNumber)) {
        console.error('MFG integration: Part name and number must be populated from API before starting quote assist');
        this.isFileUploading = false;
        this.cdr.detectChanges();
        return;
      }
      
      // if (!hasValidName || !hasValidNumber) {
      //   console.error('No valid part selected for Quote Assist');
      //   this.isFileUploading = false;
      //   this.cdr.detectChanges();
      //   return;
      // }

      if (!hasValidLeadTime) {
        console.error('Lead Time is required for all quantities before starting Quote Assist');
        this.isFileUploading = false;
        this.cdr.detectChanges();
        return;
      }
    }

    // Show loader when starting quote assist
    this.isFileUploading = true;
    this.cdr.detectChanges();

    // For other integration types or when no PDF files, proceed directly
    this.fetchMetaDataFromAPI();
  }

  /**
   * Fetch metadata from API with retry logic
   */
  private fetchMetaDataFromAPI(): void {
    const rfqId = this.quoteDetails.rfqId || '';
    const companyId = this.companyId || 0;

    // Call different service functions based on integration type
    let apiCall: Observable<any>;

    switch (this.integrationType.toUpperCase()) {
      case 'MFG':
        // For MFG integration, use getMfgIntegrationData
        apiCall = this.quoteAssistV2Service.getMetadataRev(rfqId, companyId);
        break;
      case 'MFGDIRECT':
        apiCall = this.quoteAssistV2Service.getMetadata(rfqId, companyId);
        break;
      case 'SUW':
        // For MFGDirect and SUW integrations, use getDirectIntegrationData
        apiCall = this.quoteAssistV2Service.getMetadata(rfqId, companyId);
        break;
      default:
        // Default fallback to getMetadata for backward compatibility
        apiCall = this.quoteAssistV2Service.getMetadata(rfqId, companyId);
        break;
    }

    apiCall.subscribe({
      next: (apiResponse: any) => {
        try {
          // Check if we have valid data based on integration type
          let hasValidData = false;
          
          if (this.integrationType.toUpperCase() === 'MFG') {
            // For MFG integration, check MarketplaceRfqResponse structure
            hasValidData = apiResponse.status && apiResponse.data && apiResponse.data.rfqMetaData;
          } else if (this.integrationType.toUpperCase() === 'MFGDIRECT' || this.integrationType.toUpperCase() === 'SUW') {
            // For MFGDirect and SUW integrations, check DirectRfqResponse structure
            hasValidData = apiResponse.status && apiResponse.data && (apiResponse.data.rfqPartsMetaData || apiResponse.data.rfqMetaData);
          } else {
            // For default case, check V1-style response structure
            hasValidData = apiResponse.data && apiResponse.data.rfqMetaData != undefined && 
                          apiResponse.data.rfqMetaData != null && apiResponse.data.rfqMetaData !== "";
          }

          if (hasValidData) {
            // Process the fetched data
            this.processFetchedQuoteData(apiResponse);
            // Set metadata for MFGDirect integration
            if (this.integrationType === 'MFGDirect') {
              this.quoteAssistV2Service.setMetadata(this.getCurrentPartExtractedContent());
            }
            // Get data from LLM for cost breakdown
            this.getDataFromLLM();
          } else {
            this.isFileUploading = false;
            this.cdr.detectChanges();
            console.warn('No RFQ metadata available');
          }
        } catch (error) {
          this.handleQuoteDataError(error);
        }
      },
      error: (error) => {
        this.handleQuoteDataError(error);
      }
    });
  }

  /**
   * Handle quote data fetch errors with retry logic
   */
  private handleQuoteDataError(error: any): void {
    console.error('Error fetching quote data:', error);
    
    // Retry once automatically
    if (!this.hasRetried) {
      this.hasRetried = true;
      console.log('Retrying quote data fetch...');
      setTimeout(() => {
        this.fetchMetaDataFromAPI();
      }, 2000); // 2 second delay before retry
    } else {
      // Show error message to user after retry fails
      this.isFileUploading = false;
      this.cdr.detectChanges();
      console.error('Failed to fetch quote data after retry');
      // Note: In a real implementation, you would show a toast/alert here
    }
  }

  /**
   * Process fetched quote data (handles different response structures based on integration type)
   */
  private processFetchedQuoteData(apiResponse: any): void {
    try {
      let rfqData: any = null;
      
      if (this.integrationType.toUpperCase() === 'MFG') {
        // For MFG integration, handle MarketplaceRfqResponse structure
        if (apiResponse.data && apiResponse.data.rfqMetaData) {
          try {
            rfqData = JSON.parse(apiResponse.data.rfqMetaData);
          } catch (error) {
            console.error('Error parsing MFG RFQ metadata:', error);
            return;
          }
        }
      } else if (this.integrationType.toUpperCase() === 'MFGDIRECT' || this.integrationType.toUpperCase() === 'SUW') {
        // For MFGDirect and SUW integrations, handle DirectRfqResponse structure
        if (apiResponse.data && apiResponse.data.rfqPartsMetaData) {
          try {
            rfqData = JSON.parse(apiResponse.data.rfqPartsMetaData);
          } catch (error) {
            console.error('Error parsing Direct RFQ metadata:', error);
            return;
          }
        }
      } else {
        // For default case, handle V1-style response structure
        if (apiResponse.data && apiResponse.data.rfqMetaData) {
          try {
            rfqData = typeof apiResponse.data.rfqMetaData === 'string' 
              ? JSON.parse(apiResponse.data.rfqMetaData) 
              : apiResponse.data.rfqMetaData;
          } catch (error) {
            console.error('Error parsing RFQ metadata:', error);
            return;
          }
        }
      }
      
      // Process the parsed data
      if (rfqData) {
        // Handle different data structures based on integration type
        if (this.integrationType.toUpperCase() === 'MFG') {
          // For MFG integration, look for rfqPartList in the parsed metadata
          if (rfqData && rfqData.length > 0) {
            const matchingPart = rfqData.find((part: any) => 
              part.part_name === this.currentPart.partName
            );
            
            if (matchingPart) {
              this.updateCurrentPartWithFetchedData(matchingPart);
            }
          }
        } else if (this.integrationType.toUpperCase() === 'MFGDIRECT' || this.integrationType.toUpperCase() === 'SUW') {
          // For MFGDirect and SUW integrations, look for partDetails in the parsed metadata
          if (rfqData.partDetails && rfqData.partDetails.length > 0) {
            const matchingPart = rfqData.partDetails.find((part: any) => 
              part.part_name === this.currentPart.partName
            );
            
            if (matchingPart) {
              this.updateCurrentPartWithFetchedData(matchingPart);
            }
          }
        } else {
          // For default case, use the existing logic
          if (rfqData.rfqPartList && rfqData.rfqPartList.length > 0) {
            const matchingPart = rfqData.rfqPartList.find((part: any) => 
              part.partName === this.currentPart.partName
            );
            
            if (matchingPart) {
              this.updateCurrentPartWithFetchedData(matchingPart);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error processing fetched quote data:', error);
    }
  }

  /**
   * Update current part with fetched data
   */
  private updateCurrentPartWithFetchedData(fetchedPart: any): void {
    // For MFG integration, update part name and number from fetched data if available
    if (this.integrationType === 'MFG') {
      if (fetchedPart.part_name && !this.currentPart.partName) {
        this.currentPart.partName = fetchedPart.part_name;
      }
      if (fetchedPart.part_number && !this.currentPart.partNumber) {
        this.currentPart.partNumber = fetchedPart.part_number;
      }
    }
    
    // Update part configuration with fetched data
    if (fetchedPart.configuration) {
      this.currentPart.configuration = {
        ...this.currentPart.configuration,
        ...fetchedPart.configuration
      };
    }
    
    // Update quantity pricing if available
    if (fetchedPart.quantityPricing) {
      this.currentPart.quantityPricing = fetchedPart.quantityPricing;
    }
    
    // Update files with extracted content if available
    if (fetchedPart.pdf_files && Array.isArray(fetchedPart.pdf_files)) {
      const pdfFiles: PartFile[] = fetchedPart.pdf_files.map((file: any) => ({
        fileName: file.fileName || file.file_url?.split('/').pop() || '',
        fileType: 'pdf',
        fileURL: file.file_url || file.fileUrl || '',
        extractedContent: file.extracted_content || file.extractedContent || ''
      }));
      
      // Merge with existing files, avoiding duplicates based on fileURL
      const existingFileURLs = this.currentPart.files.map(f => f.fileURL);
      const newFiles = pdfFiles.filter(file => !existingFileURLs.includes(file.fileURL));
      
      if (newFiles.length > 0) {
        this.currentPart.files.push(...newFiles);
      }
    }
    
    // Recalculate pricing
    // this.recalculatePricingForCurrentPart();
    this.emitPartsChange();
  }

  /**
   * Get data from LLM for cost breakdown
   */
  private getDataFromLLM(): void {
    if (!this.currentPart) {
      console.error('No current part available for LLM data');
      return;
    }

    // Skip AI call if data already loaded for this part
    if (this.currentPart.isAiDataLoaded) {
      console.log('AI data already loaded for this part, skipping API call');
      this.isFileUploading = false;
      this.cdr.detectChanges();
      return;
    }

    // For MFGDirect integration, use the stored metadata
    if (this.integrationType === 'MFGDirect') {
      // Check current part files for Drawing2D files with extractedContent
      let storedPayload = '';
      
      if (this.currentPart.files && this.currentPart.files.length > 0) {
        // Filter for Drawing2D files
        const drawing2DFiles = this.currentPart.files.filter(file => file.fileType === 'Drawing2D');
        
        if (drawing2DFiles.length > 0) {
          // Extract content from Drawing2D files
          const extractedContents: string[] = [];
          
          drawing2DFiles.forEach(file => {
            if (file.extractedContent) {
              const content = typeof file.extractedContent === 'string' 
                ? file.extractedContent 
                : JSON.stringify(file.extractedContent);
              extractedContents.push(content);
            }
          });
          
          // Combine multiple extracted contents if available
          if (extractedContents.length > 0) {
            storedPayload = extractedContents.join('\n\n---\n\n');
          }
        }
      }
      
      
      if (storedPayload) {
        this.getCostBreakdownFromLLM(storedPayload);
        return;
      }
    }

    // For other integrations, use the current part's extracted content
    const extractedContent = this.getCurrentPartExtractedContent();
    if (extractedContent) {
      this.getCostBreakdownFromLLM(extractedContent);
    } else {
      console.warn('No extracted content available for LLM processing');
      this.initializeBlankConfigurationForPart(this.currentPart);
      this.isFileUploading = false;
      this.cdr.detectChanges();
    }
  }

  private initializeBlankConfigurationForPart(part: PartDetail){
    const configuration = {
      materials: [
        { name: '', cost: null, costType: 'Per Unit' as 'Per Unit', reference: '' }
      ],
      tools: [
        { name: '', cost: null, costType: 'Fixed' as 'Fixed', reference: '' }
      ],
      processes: [
        { name: '', machineCost: null, setupCost: null, machineCostType: 'Fixed' as 'Fixed', setupCostType: 'Fixed' as 'Fixed', reference: '' }
      ],
      additionalCosts: [
        { name: 'Inspection', cost: null, costType: 'Fixed' as 'Per Unit', reference: '' },
        { name: 'Overhead', cost: null, costType: 'Fixed' as 'Per Unit', reference: '' }
      ]
    }
    part.configuration = configuration;
  }

  /**
   * Get cost breakdown from LLM using the provided content
   */
  private getCostBreakdownFromLLM(content: string): void {
    this.quoteAssistV2Service.getCostBreakdown(content).subscribe({
      next: (response: CostBreakdownResponse) => {
        if (response.success && response.data.costBreakdown) {
          this.updatePartWithCostBreakdown(this.currentPartIndex, response.data.costBreakdown);
          console.log('Cost breakdown generated successfully:', response);
        } else {
          console.warn('No cost breakdown data received');
        }
        this.isFileUploading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error generating cost breakdown:', error);
        this.isFileUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private getCurrentPartExtractedContent(): string {
    if (!this.currentPart || !this.currentPart.files.length) {
      return '';
    }

    let content = '';
    
    content += `Part Name: ${this.currentPart.partName}\n`;
    content += `Part Number: ${this.currentPart.partNumber}\n`;
    
    this.currentPart.files.forEach(file => {
      content += `File: ${file.fileName} (${file.fileType})\n`;
    });
    
    if (this.currentPart.notesForBuyer) {
      content += `Notes: ${this.currentPart.notesForBuyer}\n`;
    }

    if (this.currentPart.internalNotes) {
      content += `Internal Notes: ${this.currentPart.internalNotes}\n`;
    }

    content += `PDF File: ${this.currentPart.files[0].extractedContent}\n`;
    // content += `PDF File: [\"This engineering drawing represents a mechanical component known as a \\\"4.0 VC Spacer.\\\" Based on the visual and information provided on the drawing, here is a detailed analysis:\\n\\n1. **Representation of the Drawing**:\\n   - This drawing depicts a spacer with a cylindrical shape incorporating a central through-hole. The component is shown in three views: Top view, Section View A-A (cut-through along the diameter), and an Isometric view for better visualization of the three-dimensional shape.\\n\\n2. **Key Dimensions and Their Relationships**:\\n   - **Outer Diameter**: The diameter of the outer circle, as seen in the Top View, is not directly labeled, but it can be roughly estimated based on other given dimensions.\\n   - **Inner Diameter (Hole)**: The innermost diameter, which forms the through-hole, measures 0.282 inches.\\n   - **Height**: The spacer height, as seen from the Side View and Section A-A, is 0.405 inches tall from the bottom to the top surface.\\n   - **Thickness of the Rims**: This can be deduced from the section A-A retaining the measurement 0.083 inches at the top flange.\\n   - **Through-hole Depth**: This is equal to the total height, given its a through-hole.\\n\\n3. **Holes or Special Features**:\\n   - **Through-hole**: Central hole with a deburred finish for possibly accommodating a shaft or pin, with a diameter of 0.282 inches.\\n   - The terms \\\"DEBUR\\\" indicate that sharp edges around the hole are to be smoothed out to prevent damage during usage or assembly.\\n   - **Corner Breaks**: This suggests slight rounding or chamfering at the corners for ease of assembly and handling.\\n\\n4. **Material Specifications**:\\n   - The material specified for the spacer is Brass, which is noted in the block of text at the bottom of the drawing.\\n\\n5. **Important Notes or Specifications**:\\n   - **Units**: Dimensions are provided in inches.\\n   - **Tolerances**: Specific tolerances are mentioned with tighter control needed, particularly for the two-place decimals where the tolerance is ±.010 inches.\\n   - **Finish**: Specified as Brass, usually implying either a raw brass finish or a protective treatment typical for corrosion resistance and aesthetic purposes.\\n   - **Deburring**: All deburred edges suggest a requirement for smoothness and safety in handling.\\n   - **Do Not Scale Drawing**: Indicates that measurements should be taken from the figures provided rather than measuring the drawing scale.\\n   - **Proprietary Note**: The drawing contains a note highlighting that the replication or personal use without permission is prohibited, emphasizing its proprietary nature.\\n\\nThese listed elements are crucial for manufacturing, quality assurance, and ensure that the part can function correctly in its intended assembly or application.\"]\nSupplier Information: {\"companyName\":\"TestSupplier111\",\"employeeSize\":\"10-49\",\"myCapabilities\":[\"Fabrication\",\"Machining\",\"3D Printing\",\"Sand Mold Casting\",\"Investment Casting\",\"Printed Circuit Boards\",\"Wire Harness\",\"Forging\",\"Tool\",\"Die & Mold Making\",\"Injection Molding\",\"RTV Molding\",\"Rotational Molding\",\"Blow Molding\",\"Compression Molding\",\"Structural Foam Molding\",\"Thermoforming\",\"Springs & Wire Forming\",\"Stamping\",\"Sheet Metal\",\"Tube Modification\",\"Woodworking\",\"PP - Machining\",\"PP - Sheet Metal\",\"PP - Injection Molding\"],\"myEquipment\":[\"AA\",\"BB\",\"CC\",\"DD\",\"EE\",\"FF\",\"GG\",\"HH\",\"JJ\",\"KK\",\"LL\",\"MM\",\"NN\",\"OO\",\"PP\",\"QQ\",\"RR\",\"SS\",\"SW\",\"Test\",\"Test data 5th dec\",\"Test Equipment 1\",\"Test equipment 3\",\"Test Equipment2\"],\"industryServing\":[\"Supplier\"],\"myAddress\":\"new york, Production, Guam, 10030, United States\",\"zipCode\":\"10030\",\"additionalInfo\":[]}\nQuantity to be considered for the Quote Estimation: 20\n`;
    
    return content;
  }



  // ===== PUBLIC METHODS =====

  /**
   * Public method to reset component to Initial View (Quote Summary)
   * Can be called from parent component after successful quote details submission
   */
  resetToInitialView(): void {
    // For MFG integration, show file upload view instead of initial view
    if (this.integrationType === 'MFG') {
      this.forceShowInitialView = false;
      this.setCurrentView('file-upload');
      
      // Create a default part if none exists
      if (this.localParts.length === 0) {
        this.localParts.push(this.createNewPart());
      }
    } else {
      // For other integrations, use flag instead of clearing data
      this.forceShowInitialView = true;
      this.localParts = [];
    }
    this.currentPartIndex = 0;
    this.validationErrors = {};
    // Trigger change detection to show the appropriate view immediately
    this.cdr.detectChanges();
  }

  getPartClass(partIndex: number): string {
    const prefix = `part${partIndex}`;
    const hasErrors = this.validationErrors[`${prefix}_name`] || 
                     this.validationErrors[`${prefix}_number`];
    
    let classes = 'part-item';
    
    if (hasErrors) {
      classes += ' has-errors';
    }
    
    return classes;
  }

  getFieldClass(partIndex: number, fieldName: string): string {
    const prefix = `part${partIndex}`;
    const hasError = this.validationErrors[`${prefix}_${fieldName}`];
    
    let classes = 'form-control';
    
    if (hasError) {
      classes += ' error-border';
    }
    
    if (this.config.readonly) {
      classes += ' readonly';
    }
    
    return classes;
  }

  getFieldError(partIndex: number, fieldName: string): string | undefined {
    const prefix = `part${partIndex}`;
    return this.validationErrors[`${prefix}_${fieldName}`];
  }

  hasQuantityGreaterThanZero(): boolean {
    return this.currentPart?.quantityPricing?.some(pricing => pricing.quantity && pricing.quantity > 0) || false;
  }

  isFieldRequired(fieldName: string): boolean {
    return this.validationRules[fieldName]?.required || false;
  }

  getFileTypeIcon(fileType: string): string {
    const iconMap: { [key: string]: string } = {
      'pdf': '📄',
      'dwg': '📐',
      'step': '🔧',
      'stl': '��',
      'jpg': '🖼️',
      'png': '🖼️',
      'default': '📎'
    };
    
    const extension = fileType.toLowerCase().split('.').pop() || '';
    return iconMap[extension] || iconMap['default'];
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ===== TRACKBY FUNCTIONS =====

  trackByPartIndex(index: number, part: PartDetail): number {
    return index;
  }

  trackByFileIndex(index: number, file: PartFile): number {
    return index;
  }

  trackByPricingIndex(index: number, pricing: QuantityPricing): number {
    return index;
  }

  trackByMaterialIndex(index: number, material: MaterialItem): number {
    return index;
  }

  /**
   * Submit all parts at once with all their files and configurations
   * This method creates FormData with all parts in array format
   */
  public submitAllPartsWithFiles(): void {
    // Prevent double submission
    if (this.isSubmitting) {
      console.log('[Parts-Information] Submission already in progress, skipping...');
      return;
    }

    if (this.localParts.length === 0) {
      console.error('No parts available to submit');
      return;
    }

    // Validate all parts first
    this.validateAllParts();
    if (!this.isFormValid()) {
      console.error('Some parts have validation errors. Please fix them before submitting.');
      return;
    }

    // Set submission flag
    this.isSubmitting = true;

    // Show loading state
    this.isFileUploading = true;
    this.cdr.detectChanges();

    // Create FormData with all parts
    const formData = this.createFormDataForAllPartsWithFiles();

    // Log FormData contents for debugging
    console.log('[Parts-Information] Submitting all parts. FormData contents:');
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(key, `[FILE: ${value.name}]`);
      } else {
        console.log(key, value);
      }
    }

    // Submit to backend
    this.quoteAssistV2Service.uploadPartFiles(formData).subscribe({
      next: (response: any) => {
        console.log('[Parts-Information] All parts submitted successfully:', response);
        this.isFileUploading = false;
        this.isSubmitting = false; // Reset flag
        this.cdr.detectChanges();
        this.emitPartsChange();
      },
      error: (error: any) => {
        console.error('[Parts-Information] Error submitting all parts:', error);
        this.isFileUploading = false;
        this.isSubmitting = false; // Reset flag
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Create FormData for all parts including their files (Enhanced version)
   * This includes actual File objects for files that were uploaded in the current session
   */
  private createFormDataForAllPartsWithFiles(): FormData {
    const formData = new FormData();
    
    // Add basic information (same for all parts)
    formData.append('CompanyId', this.quoteDetails.companyId?.toString() || '');
    formData.append('RfqId', this.quoteDetails.rfqId?.toString() || '');
    formData.append('RfqName', this.quoteDetails.rfqName || '');
    formData.append('QuoteDate', this.quoteDetails.quoteExpiryDate || '');
    formData.append('CreatedOn', new Date().toISOString());
    formData.append('Source', this.integrationType);
    
    // Add buyer information
    formData.append('BuyerCompanyName', this.quoteDetails.companyName || '');
    formData.append('BuyerCompanyAddress', this.quoteDetails.address || '');
    formData.append('BuyerCompanyEmail', this.quoteDetails.companyEmail || '');
    formData.append('BuyerCompanyPhone', this.quoteDetails.companyPhone || '');
    formData.append('BuyerCompanyCity', this.quoteDetails.city || '');
    formData.append('BuyerCompanyState', this.quoteDetails.state || '');
    formData.append('BuyerCompanyZipCode', this.quoteDetails.zipCode || '');
    
    // Add contact ID if available
    if (this.quoteDetails.contactId) {
      formData.append('ContactId', this.quoteDetails.contactId.toString());
    }
    
    // Add all parts as individual form fields (not JSON)
    this.localParts.forEach((part, partIndex) => {
      console.log(`[Parts-Information] Processing part ${partIndex}:`, {
        partName: part.partName,
        partNumber: part.partNumber,
        filesCount: part.files.length
      });

      // Material specifications (extract from configuration)
      const materialSpecs = part.configuration.materials
        .filter(material => material.name && material.name.trim())
        .map(material => `${material.name}: ${material.cost} ${material.costType}`)
        .join(', ');
      
      // Finish array (from additional costs)
      const finishOptions = part.configuration.additionalCosts
        .filter(cost => cost.name && cost.name.trim())
        .map(cost => cost.name);
      
      // Quantity fields (from quantity pricing)
      const qty1 = part.quantityPricing[0]?.quantity || 0;
      const qty2 = part.quantityPricing[1]?.quantity || 0;
      const qty3 = part.quantityPricing[2]?.quantity || 0;
      
      // Add part details as individual form fields
      formData.append(`RfqPartDetails[${partIndex}].PartId`, partIndex.toString());
      formData.append(`RfqPartDetails[${partIndex}].PartName`, part.partName || '');
      formData.append(`RfqPartDetails[${partIndex}].PartNumber`, part.partNumber || '');
      formData.append(`RfqPartDetails[${partIndex}].Notes`, part.notesForBuyer || '');
      formData.append(`RfqPartDetails[${partIndex}].InternalNotes`, part.internalNotes || '');
      formData.append(`RfqPartDetails[${partIndex}].MaterialSpecifications`, materialSpecs);
      formData.append(`RfqPartDetails[${partIndex}].MaterialGrade`, 'Standard');
      formData.append(`RfqPartDetails[${partIndex}].Tolerance`, 'Standard');
      formData.append(`RfqPartDetails[${partIndex}].Qty1`, qty1.toString());
      formData.append(`RfqPartDetails[${partIndex}].Qty2`, qty2.toString());
      formData.append(`RfqPartDetails[${partIndex}].Qty3`, qty3.toString());
      
      // Add finish options as individual array elements
      finishOptions.forEach((finish, finishIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Finish[${finishIndex}]`, finish);
      });
      
      // Add files for this part
      part.files.forEach((file, fileIndex) => {
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileName`, file.fileName);
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileUrl`, file.fileURL);
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].FileType`, file.fileType);
        formData.append(`RfqPartDetails[${partIndex}].Files[${fileIndex}].IsUpdated`, 'false');
      });
    });
    
    console.log(`[Parts-Information] Created FormData with ${this.localParts.length} parts in individual fields format`);
    
    return formData;
  }

  /**
   * Create FormData for file upload with new payload format
   * This method handles actual file uploads with the new structure
   */
  private createFormDataForFileUpload(partIndex: number, files: File[]): FormData {
    const formData = new FormData();
    const part = this.localParts[partIndex];
    
    // Add basic part information
    formData.append('CompanyId', this.quoteDetails.companyId?.toString() || '');
    formData.append('RfqId', this.quoteDetails.rfqId?.toString() || '');
    formData.append('RfqName', this.quoteDetails.rfqName || '');
    formData.append('QuoteDate', this.quoteDetails.quoteExpiryDate || '');
    formData.append('CreatedOn', new Date().toISOString());
    formData.append('Source', this.integrationType);
    
    // Add buyer information
    formData.append('BuyerCompanyName', this.quoteDetails.companyName || '');
    formData.append('BuyerCompanyAddress', this.quoteDetails.address || '');
    formData.append('BuyerCompanyEmail', this.quoteDetails.companyEmail || '');
    formData.append('BuyerCompanyPhone', this.quoteDetails.companyPhone || '');
    formData.append('BuyerCompanyCity', this.quoteDetails.city || '');
    formData.append('BuyerCompanyState', this.quoteDetails.state || '');
    formData.append('BuyerCompanyZipCode', this.quoteDetails.zipCode || '');
    
    // Add contact ID if available
    if (this.quoteDetails.contactId) {
      formData.append('ContactId', this.quoteDetails.contactId.toString());
    }
    
    // Add all parts as individual form fields (not JSON)
    this.localParts.forEach((currentPart, index) => {
      // Material specifications (extract from configuration)
      const materialSpecs = currentPart.configuration.materials
        .filter(material => material.name && material.name.trim())
        .map(material => `${material.name}: ${material.cost} ${material.costType}`)
        .join(', ');
      
      // Finish array (from additional costs)
      const finishOptions = currentPart.configuration.additionalCosts
        .filter(cost => cost.name && cost.name.trim())
        .map(cost => cost.name);
      
      // Quantity fields (from quantity pricing)
      const qty1 = currentPart.quantityPricing[0]?.quantity || 0;
      const qty2 = currentPart.quantityPricing[1]?.quantity || 0;
      const qty3 = currentPart.quantityPricing[2]?.quantity || 0;
      
      // Add part details as individual form fields
      formData.append(`RfqPartDetails[${index}].PartId`, (index + 1).toString());
      formData.append(`RfqPartDetails[${index}].PartName`, currentPart.partName || '');
      formData.append(`RfqPartDetails[${index}].PartNumber`, currentPart.partNumber || '');
      formData.append(`RfqPartDetails[${index}].Notes`, currentPart.notesForBuyer || '');
      formData.append(`RfqPartDetails[${index}].InternalNotes`, currentPart.internalNotes || '');
      formData.append(`RfqPartDetails[${index}].MaterialSpecifications`, materialSpecs);
      formData.append(`RfqPartDetails[${index}].MaterialGrade`, 'Standard');
      formData.append(`RfqPartDetails[${index}].Tolerance`, 'Standard');
      formData.append(`RfqPartDetails[${index}].Qty1`, qty1.toString());
      formData.append(`RfqPartDetails[${index}].Qty2`, qty2.toString());
      formData.append(`RfqPartDetails[${index}].Qty3`, qty3.toString());
      formData.append(`RfqPartDetails[${index}].IsFileSkipped`, this.localParts[index].isContinueWithoutFiles?.toString() || 'false');
      
      // Add finish options as individual array elements
      finishOptions.forEach((finish, finishIndex) => {
        formData.append(`RfqPartDetails[${index}].Finish[${finishIndex}]`, finish);
      });
      
      // Add files for this part
      if (index === partIndex) {
        // For the current part being uploaded, add the new files
        files.forEach((file, fileIndex) => {
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].File`, file);
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileName`, file.name);
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileType`, this.getFileType(file.name));
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].IsUpdated`, 'true');
        });
      } else {
        // For other parts, add existing files
        currentPart.files.forEach((file, fileIndex) => {
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileName`, file.fileName);
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileUrl`, file.fileURL);
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].FileType`, file.fileType);
          formData.append(`RfqPartDetails[${index}].Files[${fileIndex}].IsUpdated`, 'false');
        });
      }
    });

    console.log(`[Parts-Information] Created FormData for file upload with ${this.localParts.length} parts in individual fields format`);

    return formData;
  }

  // Tooltip methods
  showTooltip(event: MouseEvent, text: string = 'Use the "Reference" field for internal notes regarding this item of your quote') {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.tooltipPosition = {
      x: rect.left + rect.width / 2,
      y: rect.top - 75
    };
    this.tooltipText = text;
    this.isTooltipVisible = true;
    this.cdr.detectChanges();
  }

  hideTooltip() {
    this.isTooltipVisible = false;
    this.cdr.detectChanges();
  }

  public hasSectionFieldError(sectionName: string, itemIndex: number, fieldName: 'name' | 'cost'): boolean {
    return this.sectionValidationErrors[sectionName]?.[itemIndex]?.[fieldName] || false;
  }

  private clearSectionFieldError(sectionName: string, itemIndex: number, fieldName: 'name' | 'cost'): void {
    if (this.sectionValidationErrors[sectionName]?.[itemIndex]) {
      this.sectionValidationErrors[sectionName][itemIndex][fieldName] = false;
    }
  }

  public hasLeadTimeError(itemIndex: number): boolean {
    return this.sectionValidationErrors['leadtime']?.[itemIndex]?.['cost'] || false;
  }

  public hasQuantityError(partIndex: number): boolean {
    return !!this.getFieldError(partIndex, 'quantity');
  }

  public hasTotalError(partIndex: number, rowIndex?: number): boolean {
    const hasError = !!this.getFieldError(partIndex, 'total');
    
    // If no rowIndex provided, return original behavior
    if (rowIndex === undefined) {
      return hasError;
    }
    
    // Only show error for rows where quantity > 0
    const part = this.localParts[partIndex];
    if (part?.quantityPricing?.[rowIndex]) {
      const row = part.quantityPricing[rowIndex];
      return hasError && !!row.quantity && row.quantity > 0;
    }
    
    return false;
  }

  public hasUnitPriceError(partIndex: number, rowIndex?: number): boolean {
    const hasError = !!this.getFieldError(partIndex, 'unitPrice');
    
    // If no rowIndex provided, return original behavior
    if (rowIndex === undefined) {
      return hasError;
    }
    
    // Only show error for rows where quantity > 0
    const part = this.localParts[partIndex];
    if (part?.quantityPricing?.[rowIndex]) {
      const row = part.quantityPricing[rowIndex];
      return hasError && !!row.quantity && row.quantity > 0;
    }
    
    return false;
  }

  public hasUnitPriceTotalError(partIndex: number): boolean {
    return !!this.getFieldError(partIndex, 'unitPriceTotal');
  }

  private hasValidPricingForCurrentPart(field: 'quantity' | 'total'): boolean {
    return this.currentPart?.quantityPricing?.some(pricing => pricing[field] && pricing[field] > 0) || false;
  }

  private hasValidTotalForCurrentPart() {
    if(this.hasValidPricingForCurrentPart('total')){
      this.clearValidationError('total');
    }
  }

  private clearLeadTimeValidationError(itemIndex: number): void {
    if (this.sectionValidationErrors['leadtime']?.[itemIndex]) {
      this.sectionValidationErrors['leadtime'][itemIndex]['cost'] = false;
    }
  }

  private clearValidationError(field: 'quantity' | 'total'): void {
    const prefix = `part${this.currentPartIndex}`;
    delete this.validationErrors[`${prefix}_${field}`];
  }

  private getMixPanelEventForIntegration(integrationType: 'MFG' | 'MFGDirect' | 'SUW'): string {
    switch (integrationType) {
      case 'MFG':
        return MixPanelEvents.PartQuotingCompletedMarketPlace;
      case 'MFGDirect':
        return MixPanelEvents.PartQuotingCompletedSelfCreated;
      case 'SUW':
        return MixPanelEvents.PartQuotingCompletedWebsiteSubmitted;
      default:
        return MixPanelEvents.PartQuotingCompletedSelfCreated;
    }
  }
}